/**
 * The driver control room. Admin only.
 *
 * Owns what GET /api/v1/backfill/driver emits for one (company, env):
 * which tables, in what order, and which operator SQLI runs alongside.
 * With no active plan the driver falls back to the full sync, and that
 * sentence is on screen rather than only in the docs.
 *
 * Exposed as window.SyncControlPage.
 */
import { confirmAction, promptAction } from './feedback.jsx';

const { useState, useEffect, useMemo, useRef, useCallback, useId } = React;

// Each entry in build_static.sh is bundled as its own IIFE, so soc-app.jsx's
// `cls` is NOT in scope here. Define it rather than reaching for a global
// that does not exist -- the failure is an undefined-function throw during
// render, which blanks the page.
const cls = (...xs) => xs.filter(Boolean).join(' ');

/* One picker, five call sites. Not a <select>: the table list is ~3,200
 * entries. Not free text: a free-text env box is how production got a plan
 * bound to 'oftov' when the only real env is 'oftovp', silently, forever.
 *
 * Only a LISTED row can be chosen -- `value` is set by onPick and cleared by
 * typing, so a caller's Add button can gate on it and nothing else. `items`
 * may carry disabled rows; they are SHOWN, because "no generated script" and
 * "no such table" send an operator to different places.
 *
 * `onQuery` is optional. Given, the caller owns filtering (forms search on
 * the server); omitted, the list is filtered here (tables arrive whole).
 */
const MAX_ROWS = 50;

function Typeahead({
  label, hint, items, total, value, onPick, onQuery, placeholder, lang, emptyText,
}) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef(null);
  const curRef = useRef(null);
  // useId, not a module counter: five of these live on one page, and an
  // input whose htmlFor collides with another's is labelled by neither.
  const uid = useId();
  const inputId = `${uid}in`;
  const listId = `${uid}list`;
  const hintId = `${uid}hint`;

  const filtered = useMemo(() => {
    if (onQuery) return items || [];       // the caller already filtered
    const q = text.trim().toUpperCase();
    if (!q) return items || [];
    return (items || []).filter((it) => it.name.toUpperCase().includes(q));
  }, [items, text, onQuery]);
  const shown = filtered.slice(0, MAX_ROWS);
  // `total` is the SERVER's unpaged count and wins when supplied -- but only
  // in onQuery mode. Without it the footer can never fire there: the caller
  // asks for at most MAX_ROWS rows, so filtered.length never exceeds
  // shown.length and the affordance spec 5.8 requires would silently not
  // exist. In local mode `total` would be the UNfiltered count and would
  // overstate the remainder on every keystroke, so filtered.length wins.
  const hidden = Math.max(
    0, ((onQuery && total != null) ? total : filtered.length) - shown.length,
  );
  // `items` can shrink under an in-flight onQuery response while the cursor
  // still sits deep in the previous list. Clamp on the way out rather than in
  // an effect, which would race the very update it is correcting -- and read
  // the arrow keys off this clamped value, or ArrowUp from a stale 60 walks
  // fifty invisible steps before it moves anything on screen.
  const cur = shown.length ? Math.min(cursor, shown.length - 1) : 0;

  const type = (v) => {
    setText(v);
    setOpen(true);
    setCursor(0);
    // Typing invalidates a previous pick: the caller's button must not stay
    // enabled against a name the operator has since edited away from.
    if (value) onPick(null);
    if (onQuery) onQuery(v);
  };
  const choose = (it) => {
    if (!it || it.disabled) return;
    onPick(it);
    setText(it.name);
    setOpen(false);
  };

  // The parent clears `value` after a successful add. Without this the box
  // still reads TRANSORDER while the Add button is disabled, and nothing on
  // screen says why -- the exact "cannot mistake" failure this page exists to
  // remove.
  //
  // It clears the box only while the box still shows the name that was just
  // cleared. `type()` ALSO clears `value` -- on every keystroke after a pick --
  // and a bare `if (prevValue.current && !value)` cannot tell the two apart, so
  // it wipes the operator's edit the moment they backspace one character off a
  // picked name. Comparing against the outgoing name separates them with no
  // flag to get stuck: a stale label still reads TRANSORDER, an edit does not.
  // `text` is deliberately out of the dep array -- the trigger is `value`
  // changing, and re-running on every keystroke would re-introduce the wipe.
  const prevValue = useRef(value);
  useEffect(() => {
    const was = prevValue.current;
    if (was && !value && text === was.name) setText('');
    prevValue.current = value;
  }, [value]);
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setCursor(Math.max(0, Math.min(cur + 1, shown.length - 1))); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(Math.max(0, cur - 1)); }
    // Only while the list is open. A closed typeahead must let Enter through
    // to whatever the caller wrapped around it.
    else if (e.key === 'Enter') { if (!open) return; e.preventDefault(); choose(shown[cur]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  // Fifty rows in a 264px box: arrow-keying past the seventh moves the
  // highlight out of sight, and a cursor the operator cannot see is not a
  // cursor. `nearest` scrolls the list rather than jumping the page.
  useEffect(() => {
    const el = curRef.current;
    if (open && el && el.isConnected) el.scrollIntoView({ block: 'nearest' });
  }, [cur, open]);

  // Close on an outside click. Without this the list stays over the panel
  // below it and swallows the next click the operator makes.
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  // Same job for the keyboard: Tab out and the list must not stay floating
  // over the panel below. Rows are taken on mousedown with preventDefault, so
  // a click never lands here -- only a genuine focus loss does.
  const leave = (e) => {
    if (boxRef.current && boxRef.current.contains(e.relatedTarget)) return;
    setOpen(false);
  };

  const picked = !!value;
  return (
    <div className="sync-ta" ref={boxRef}>
      <label className="sync-field-label" htmlFor={inputId} dir="auto">{label}</label>
      {/* The list anchors to the INPUT, not to the field: anchored to the
          field it would open below the hint line, detached from the box it
          belongs to by the height of a sentence. */}
      <div className={cls('sync-ta-anchor', picked && 'picked')}>
        <input
          id={inputId} className="set-input sync-ta-input" dir="auto"
          value={text} placeholder={placeholder || ''}
          role="combobox" aria-expanded={open} aria-autocomplete="list"
          aria-haspopup="listbox" aria-controls={open ? listId : undefined}
          aria-activedescendant={open && shown.length ? `${uid}o${cur}` : undefined}
          aria-describedby={hint ? hintId : undefined}
          autoComplete="off" spellCheck={false}
          onChange={(e) => type(e.target.value)} onKeyDown={onKey}
          onFocus={() => setOpen(true)} onBlur={leave}
          onClick={() => setOpen(true)}
        />
        {picked && (
          /* Armed, without a word. `value` is set only by picking a listed
             row, so this mark is the one thing on screen separating
             "TRANSORDER is chosen" from "TRANSORDER is merely typed". The
             sentence belongs to the caller's Add button; this answers the
             glance. */
          <svg className="sync-ta-armed" viewBox="0 0 16 16" aria-hidden="true"
               fill="none" stroke="currentColor" strokeWidth="2.2"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 8.4 6.4 11.8 13 4.6" />
          </svg>
        )}
        {open && (
          <div className="sync-ta-pop">
            {/* A listbox may hold only options, so the empty line and the
                +N footer are siblings of it rather than rows inside it, and
                the listbox stays mounted at zero rows so the input's
                aria-controls resolves to something real. */}
            <div className="sync-ta-list" role="listbox" id={listId}>
              {shown.map((it, i) => (
                <div key={it.name} role="option" id={`${uid}o${i}`}
                     ref={i === cur ? curRef : undefined}
                     aria-selected={i === cur}
                     aria-disabled={it.disabled ? true : undefined}
                     className={cls('sync-ta-row', i === cur && 'cur',
                                    it.disabled && 'sync-ta-off')}
                     title={it.disabled ? it.disabledWhy : undefined}
                     onMouseDown={(e) => { e.preventDefault(); choose(it); }}>
                  <span className="mono sync-ta-name" dir="auto">{it.name}</span>
                  {it.hint && <span className="sync-ta-hint" dir="auto">{it.hint}</span>}
                  {it.disabled && (
                    <span className="sync-ta-why" dir="auto">{it.disabledWhy}</span>
                  )}
                </div>
              ))}
            </div>
            {shown.length === 0 && (
              <div className="sync-ta-empty" role="status" dir="auto">
                {emptyText || (lang === 'he' ? 'אין התאמות' : 'no matches')}
              </div>
            )}
            {hidden > 0 && (
              <div className="sync-ta-empty sync-ta-more" dir="auto">
                {lang === 'he' ? `+${hidden} נוספות — המשך להקליד`
                               : `+${hidden} more — keep typing`}
              </div>
            )}
          </div>
        )}
      </div>
      {hint && <div className="sync-field-hint" id={hintId} dir="auto">{hint}</div>}
    </div>
  );
}

// Highlighting inside a <textarea> is impossible, so the warning layer is a
// <pre> mirrored behind it with identical font, padding and box sizing, and
// one shared scroll offset. If these ever drift apart the marks land on the
// wrong words, which is worse than no marks -- so the two elements MUST keep
// their styling in one CSS rule (.sync-editor, .sync-editor-mirror).
function DeleteAwareEditor({ value, onChange, lang }) {
  const taRef = useRef(null);
  const preRef = useRef(null);
  // DELETE is highlighted because the user asked for it; UPDATE is counted
  // beside it because a highlight on DELETE alone teaches that DELETE is THE
  // dangerous statement, while `UPDATE ... SET` with no WHERE sits unmarked
  // next to it -- against the live ERP, not the mirror.
  const hits = useMemo(() => (value.match(/\bDELETE\b/gi) || []).length, [value]);
  const updates = useMemo(() => (value.match(/\bUPDATE\b/gi) || []).length, [value]);
  const marked = useMemo(() => {
    const parts = [];
    let last = 0;
    const re = /\bDELETE\b/gi;
    let m;
    while ((m = re.exec(value)) !== null) {
      parts.push(value.slice(last, m.index));
      parts.push(<mark className="sql-delete" key={m.index}>{m[0]}</mark>);
      last = m.index + m[0].length;
    }
    parts.push(value.slice(last));
    return parts;
  }, [value]);
  const onScroll = useCallback(() => {
    if (preRef.current && taRef.current) {
      preRef.current.scrollTop = taRef.current.scrollTop;
      preRef.current.scrollLeft = taRef.current.scrollLeft;
    }
  }, []);
  // The warnings are siblings of .sync-editor-wrap, NOT children: the wrap's
  // two overlay layers are position:absolute so the wrap's own box height
  // comes only from its min-height, and an in-flow child placed INSIDE that
  // box renders at its top edge -- squarely on top of the text instead of
  // below it. Keeping them outside is what makes the layout below correct.
  return (
    <>
      <div className="sync-editor-wrap">
        <pre className="sync-editor-mirror" ref={preRef} aria-hidden="true" dir="auto">{marked}</pre>
        <textarea
          aria-label={lang === 'he' ? 'קוד הסקריפט' : 'Script code'} className="sync-editor" ref={taRef} value={value} dir="auto" spellCheck={false}
          onScroll={onScroll} onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {hits > 0 && (
        <div className="sync-warn" dir="auto">
          {lang === 'he'
            ? `הסקריפט מכיל DELETE (${hits}). כל DELETE חייב לציין FROM ולשאת WHERE — אחרת השרת יסרב לשמור. למחיקה מכוונת ללא WHERE, כתוב בתוך אותה פקודה: /* PM:ALLOW-BARE-DELETE */`
            : `This script contains DELETE (${hits}). Every DELETE must name its table with FROM and carry a WHERE — the server refuses to save it otherwise. For a deliberately unbounded delete, write /* PM:ALLOW-BARE-DELETE */ inside that same statement.`}
        </div>
      )}
      {updates > 0 && (
        <div className="sync-warn" dir="auto">
          {lang === 'he'
            ? `הסקריפט מכיל UPDATE (${updates}). השרת אינו בודק UPDATE — ודא ש-WHERE קיים.`
            : `This script contains UPDATE (${updates}). The server does NOT check UPDATE — make sure yours carries a WHERE.`}
        </div>
      )}
    </>
  );
}

// --- 8.1 --------------------------------------------------------------
// One plan per row. Exactly one may be active; when none is, the fallback
// sentence below is the feature's most important documentation and belongs
// on screen rather than only in a doc.
function PlansPanel({
  tenantId, env, envs, lang, plans, selectedId, onPick, reload, onError,
}) {
  const [name, setName] = useState('');
  const act = (p, on) => PCMAdapter.setSyncPlanActive(tenantId, p.id, on)
    .then(reload).catch((e) => onError(String(e.message || e)));
  return (
    <div className="sync-panel">
      <h3 dir="auto">{lang === 'he' ? '2 · תוכניות' : '2 · Plans'}</h3>
      <div className="sync-sub" dir="auto">
        {lang === 'he'
          ? 'רשות. תוכנית פעילה מחליפה את הסנכרון המלא: רק הטבלאות שבה נסרקות, לפי הסדר שבה. רק תוכנית אחת יכולה להיות פעילה, ורק היא משפיעה על הדרייבר.'
          : 'Optional. An active plan replaces the fallback: only its tables are walked, in its order. Only ONE plan can be active, and only the active plan affects the driver.'}
      </div>
      {plans.map((p) => {
        const orphan = envs.length > 0 && !envs.includes(p.env);
        const tables = p.members.filter((m) => m.kind === 'table').length;
        const scripts = p.members.filter((m) => m.kind === 'script').length;
        return (
          <div key={p.id}
               className={cls('sync-plan-row', p.is_active && 'active',
                              p.id === selectedId && 'sel')}
               onClick={() => onPick(p.id)}>
            <span dir="auto">{p.name}</span>
            <span className="mono">{p.env}</span>
            {orphan && (
              <span className="sync-warn-inline" dir="auto">
                {lang === 'he' ? 'אין נתונים לסביבה זו' : 'no data for this env'}
              </span>
            )}
            <span className="mono">{p.members.length}</span>
            {p.is_active && (
              <span className="sync-badge" dir="auto">
                {lang === 'he' ? 'פעילה' : 'Active'}
              </span>
            )}
            <button className="tab"
                    disabled={!p.is_active && orphan}
                    title={!p.is_active && orphan
                      ? (lang === 'he'
                          ? `לסביבה '${p.env}' אין ריצות טבלה — התוכנית לא תפיק דבר`
                          : `env '${p.env}' has no table runs — this plan can never emit anything`)
                      : undefined}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!p.is_active && !await confirmAction(
                        lang === 'he'
                          ? `להפעיל את "${p.name}"? מהמעבר הבא הדרייבר יסרוק רק את ${tables} הטבלאות האלה, בסדר הזה, ויריץ ${scripts} סקריפטים בכל מעבר. הסנכרון המלא ייפסק.`
                          : `Activate "${p.name}"? From the next driver pass the driver walks only these ${tables} tables, in this order, and runs ${scripts} script(s) on every pass. The fallback full sync stops.`,
                        { lang, confirmLabel: lang === 'he' ? 'הפעלה' : 'Activate' }
                      )) return;
                      act(p, !p.is_active);
                    }}>
              {p.is_active ? (lang === 'he' ? 'כבה' : 'Deactivate')
                           : (lang === 'he' ? 'הפעל' : 'Activate')}
            </button>
            <button className="tab" onClick={async (e) => {
              e.stopPropagation();
              if (!await confirmAction(lang === 'he' ? `למחוק את התוכנית "${p.name}"?` : `Delete plan "${p.name}"?`, { lang, danger: true, confirmLabel: lang === 'he' ? 'מחיקה' : 'Delete' })) return;
              PCMAdapter.deleteSyncPlan(tenantId, p.id).then(reload)
                .catch((err) => onError(String(err.message || err)));
            }}>{lang === 'he' ? 'מחק' : 'Delete'}</button>
          </div>
        );
      })}
      <div className="sync-row sync-plan-new">
        <input className="set-input" value={name} dir="auto"
               placeholder={lang === 'he' ? 'שם תוכנית' : 'plan name'}
               onChange={(e) => setName(e.target.value)}/>
        <button className="tab" disabled={!name.trim() || !env}
                title={!env ? (lang === 'he' ? 'אין סביבה' : 'no environment') : undefined}
                onClick={() => {
                  PCMAdapter.createSyncPlan(tenantId, { env, name: name.trim(), description: '' })
                    .then(() => { setName(''); reload(); })
                    .catch((e) => onError(String(e.message || e)));
                }}>
          {lang === 'he' ? `צור בסביבה ${env}` : `Create in ${env}`}
        </button>
      </div>
    </div>
  );
}

// A gate, not a note: an ungated table with a failed pre-script would be
// walked against data the script never populated, reconcile 'ok' over zero
// windows and be written complete. Spec 5.5 puts it on BOTH hook selects, so
// the sentence lives here once rather than in two places that can drift.
const HOOK_GATE = {
  he: 'אם סקריפט ה"לפני" נכשל, הטבלה מדולגת במעבר הזה במקום להיסרק על נתונים שלא הוכנו.',
  en: 'If a pre-script fails, this table is skipped this pass rather than walked against data the script did not populate.',
};

const FORK_WARN = {
  en: "A fork REPLACES this table's generated script and owns its envelope. "
    + "Change the source = 'T_<TNAME>' line inside it and this table's run "
    + 'bookkeeping breaks silently.',
  he: 'פיצול מחליף את הסקריפט המיוצר של הטבלה ומחזיק את המעטפת שלו. '
    + "שינוי שורת ה-source = 'T_<TNAME>' בתוכו שובר בשקט את מעקב הריצות של הטבלה.",
};

// --- 8.2 --------------------------------------------------------------
// The members editor. Order is (position, id) server-side, so the whole
// array is sent on every save -- that is what makes reordering atomic.
// A member whose `runnable` is false is greyed; `null` means the bundle is
// not mounted and must NOT read as "no script".
function MembersPanel({
  tenantId, lang, plan, scripts, tables, rows, setRows, reload, onError,
}) {
  const [preview, setPreview] = useState(null);
  // Standalone script member add. Kept separate from the table typeahead's
  // own pick -- a script member has no table name at all.
  const [scriptToAdd, setScriptToAdd] = useState('');
  // Every hook in this component sits ABOVE the `if (!plan) return` below.
  // `plan` flips from null to an object on first selection; a hook under that
  // line changes the hook count between renders and React throws.
  const [pickedTable, setPickedTable] = useState(null);
  const [formItems, setFormItems] = useState([]);
  const [formTotal, setFormTotal] = useState(null);
  const [pickedForm, setPickedForm] = useState(null);
  const queryRef = useRef(null);
  // The same reqId + live-key pair RefreshPolicyPanel uses below, for the
  // same reason and with the same two checks. The counter proves a response
  // is not stale relative to a LATER request; the refs prove it still
  // matches what is on screen right now, which the counter alone cannot --
  // and here there are two ways for that to happen that fire no new request
  // at all. A company change fires none (the operator has to type again),
  // so without formTenantRef the old company's forms land in the list. And
  // the 200 ms debounce means the box can already read a different word
  // while an earlier request is in flight, with the later request not yet
  // fired to bump the counter -- so formQRef is what stops 'ord' results
  // from filling a box that now says 'orders'.
  const formReqRef = useRef(0);
  const formTenantRef = useRef(tenantId);
  formTenantRef.current = tenantId;
  const formQRef = useRef('');
  // A pending debounce outlives this component. Nothing here renders after
  // an unmount, so the setState would be inert, but the timer is ours to
  // clear and a lookup fired for a page nobody is looking at is a request
  // against the ERP's collector for nothing.
  useEffect(() => () => clearTimeout(queryRef.current), []);
  const dirty = useMemo(
    () => JSON.stringify(rows) !== JSON.stringify(plan ? plan.members : []),
    [rows, plan]);
  if (!plan) return <div className="sync-panel">{lang === 'he' ? 'בחר תוכנית' : 'Pick a plan'}</div>;

  const addTable = () => {
    if (!pickedTable) return;
    const t = pickedTable.name;
    // A silent skip, not an error -- the same way the resolve preview's
    // "Add checked" refuses a duplicate.
    if (!rows.some((r) => r.kind === 'table' && r.tname === t)) {
      setRows(rows.concat([{ kind: 'table', tname: t, source_ename: null }]));
    }
    setPickedTable(null);
  };

  // Debounced: form titles are searched on the server. 200 ms is short
  // enough not to feel laggy and long enough that a typed word is one call.
  const onFormQuery = (q) => {
    // Recorded synchronously, on the keystroke -- not inside the timeout,
    // which is exactly the window this is here to see across.
    formQRef.current = q;
    if (queryRef.current) clearTimeout(queryRef.current);
    queryRef.current = setTimeout(() => {
      const myId = ++formReqRef.current;
      // Read at FIRE time, never from this closure: the closure's tenantId
      // is whatever it was when the key was pressed, so a company change
      // inside the 200 ms window would send this lookup to the company the
      // operator has already left.
      const myTenant = formTenantRef.current;
      if (!myTenant) return;
      PCMAdapter.getSyncLookupForms(myTenant, q, 50)
        .then((d) => {
          if (myId !== formReqRef.current) return;   // superseded
          if (myTenant !== formTenantRef.current || q !== formQRef.current) return;
          setFormTotal(d.total);
          setFormItems(d.forms.map((f) => ({
            name: f.ename,
            hint: f.title_he || f.title_en || '',
          })));
        })
        .catch((e) => {
          if (myId !== formReqRef.current) return;
          if (myTenant !== formTenantRef.current || q !== formQRef.current) return;
          onError(String(e.message || e));
        });
    }, 200);
  };

  // Spec §1's capability 2 -- operator SQLI run alongside the table walks,
  // in addition to what the driver does. A script member has no completion
  // state (see plan_emission in backfill_driver) -- it runs on every pass.
  const addScript = () => {
    if (!scriptToAdd) return;
    setRows(rows.concat([{ kind: 'script', script_id: Number(scriptToAdd) }]));
    setScriptToAdd('');
  };

  // '' (the "none" option) must clear the field to null, not leave it
  // undefined -- PUT /members distinguishes them, and service.py's
  // _TABLE_ONLY_FIELDS check only fires on a field that is not None.
  const setHook = (i, field, value) => {
    const next = rows.slice();
    next[i] = { ...rows[i], [field]: value ? Number(value) : null };
    setRows(next);
  };

  const move = (i, d) => {
    const next = rows.slice();
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
  };
  const save = () => PCMAdapter.putSyncPlanMembers(tenantId, plan.id, rows.map((r) => ({
    kind: r.kind, tname: r.tname, script_id: r.script_id,
    pre_script_id: r.pre_script_id, post_script_id: r.post_script_id,
    fork_script_id: r.fork_script_id, source_ename: r.source_ename,
  }))).then(reload).catch((e) => onError(String(e.message || e)));

  const scriptOf = (id) => scripts.find((s) => s.id === id) || null;
  const nameOf = (id) => (scriptOf(id) || {}).name || '';

  // The member is edited locally (rows/setRows), not saved -- forking is a
  // library write (a new sync_scripts row) that the operator still has to
  // attach via Save, same as any other row edit here.
  const doFork = async (i, m) => {
    const suggested = `${m.tname}_fork`;
    const forkName = await promptAction(
      `${FORK_WARN[lang === 'he' ? 'he' : 'en']}\n\n`
      + (lang === 'he' ? 'שם הסקריפט המפוצל:' : 'Fork script name:'), suggested,
      { lang, confirmLabel: lang === 'he' ? 'פיצול' : 'Fork' });
    if (!forkName || !forkName.trim()) return;
    PCMAdapter.forkSyncScript(tenantId, { tname: m.tname, name: forkName.trim() })
      .then((s) => {
        const next = rows.slice();
        next[i] = { ...m, fork_script_id: s.id };
        setRows(next);
        return reload();
      })
      .catch((e) => onError(String(e.message || e)));
  };

  return (
    <div className="sync-panel">
      <h3 dir="auto">{lang === 'he' ? 'תוכן התוכנית' : 'Plan contents'}</h3>
      <div className="sync-sub" dir="auto">
        {lang === 'he' ? 'רץ מלמעלה למטה. שנה סדר עם ▲▼ ואז שמור סדר.'
                       : 'Runs top to bottom. Reorder with ▲▼, then Save order.'}
      </div>
      {rows.map((m, i) => (
        <div key={m.id || `${m.kind}:${m.tname || m.script_id}:${i}`}
             className={cls('sync-member', m.runnable === false && 'sync-member-unrunnable')}>
          <button className="tab" onClick={() => move(i, -1)}>▲</button>
          <button className="tab" onClick={() => move(i, 1)}>▼</button>
          <span className="mono">{m.kind}</span>
          <span dir="auto">{m.tname || nameOf(m.script_id)}</span>
          {/* A standalone script member has no form_runs row -- it runs on
              EVERY driver pass while this plan is active, not once. */}
          {m.kind === 'script' && (
            <span className="sync-member-hook" dir="auto">
              {lang === 'he' ? 'רץ בכל מעבר של הדרייבר' : 'runs on every driver pass'}
            </span>
          )}
          {/* Hooks only make sense on a table member -- service.py's
              Conflict on _TABLE_ONLY_FIELDS is what enforces that server-
              side, so the UI must not be able to build a script row that
              trips it. */}
          {m.kind === 'table' && (
            <label className="sync-member-hook" dir="auto"
                   title={HOOK_GATE[lang === 'he' ? 'he' : 'en']}>
              {lang === 'he' ? 'לפני' : 'pre'}:
              <select className="set-input" value={m.pre_script_id || ''}
                      onChange={(e) => setHook(i, 'pre_script_id', e.target.value)}>
                <option value="">{lang === 'he' ? 'ללא' : 'none'}</option>
                {scripts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          )}
          {m.kind === 'table' && (
            <label className="sync-member-hook" dir="auto"
                   title={HOOK_GATE[lang === 'he' ? 'he' : 'en']}>
              {lang === 'he' ? 'אחרי' : 'post'}:
              <select className="set-input" value={m.post_script_id || ''}
                      onChange={(e) => setHook(i, 'post_script_id', e.target.value)}>
                <option value="">{lang === 'he' ? 'ללא' : 'none'}</option>
                {scripts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          )}
          {m.kind === 'table' && !m.fork_script_id && (
            <button className="tab" title={FORK_WARN[lang === 'he' ? 'he' : 'en']}
                    onClick={() => doFork(i, m)}>
              {lang === 'he' ? 'פצל' : 'Fork'}
            </button>
          )}
          {m.fork_script_id && (
            <span className="sync-member-hook" dir="auto">
              fork: {nameOf(m.fork_script_id)}
              {/* Drift is read from the fork script's own server-computed
                  `drifted` field -- never recomputed here. The stored
                  forked_from_sha256 is a digest of the generated script's
                  cp1255 BYTES; the browser holds only the decoded string and
                  TextEncoder emits UTF-8 only, so a client-side digest would
                  never match and this would read as permanently drifted. */}
              {scriptOf(m.fork_script_id) && scriptOf(m.fork_script_id).drifted && (
                <span className="sync-drift" dir="auto">
                  {' — '}
                  {lang === 'he' ? 'הסקריפט המיוצר השתנה מאז הפיצול'
                                 : 'the generated script has changed since this fork'}
                </span>
              )}
            </span>
          )}
          {m.runnable === false && (
            <span className="sync-warn">{lang === 'he' ? 'אין סקריפט מיוצר' : 'no generated script'}</span>
          )}
          {m.runnable === null && (
            <span className="sync-warn">{lang === 'he' ? 'החבילה אינה מותקנת' : 'bundle not mounted'}</span>
          )}
          <button className="tab" onClick={() => setRows(rows.filter((_, k) => k !== i))}>×</button>
        </div>
      ))}
      {/* The three ways INTO the list, grouped and evenly spaced: two of
          them are .sync-row (margin-top: 8px) and one is a bare fieldset, so
          ungrouped they sit 16px, 8px, 16px apart and the middle one reads
          as belonging to the control above it rather than as its peer. */}
      <div className="sync-adds">
        <div className="sync-row sync-formrow">
          <Typeahead
            lang={lang}
            label={lang === 'he' ? 'הוסף טבלה' : 'Add a table'}
            hint={lang === 'he'
              ? 'נסרקת פעם אחת ואז מסומנת כהושלמה, ויוצאת מתור העבודה של התוכנית.'
              : "Walked once, then marked complete. It leaves the plan's work queue when it finishes."}
            items={bundleItems(tables, lang)}
            value={pickedTable}
            onPick={setPickedTable}
            emptyText={tablesEmptyText(tables, lang)}
          />
          <button className="tab" disabled={!pickedTable}
                  title={!pickedTable ? (lang === 'he' ? 'בחר טבלה מהרשימה' : 'pick a table from the list') : undefined}
                  onClick={addTable}>
            {lang === 'he' ? 'הוסף' : 'Add'}
          </button>
        </div>
        <div className="sync-fieldset">
          <label className="sync-field-label" dir="auto">
            {lang === 'he' ? 'הוסף סקריפט' : 'Add a script'}
          </label>
          <div className="sync-row">
            <select className="set-input" value={scriptToAdd}
                    onChange={(e) => setScriptToAdd(e.target.value)}>
              <option value="">{lang === 'he' ? 'בחר סקריפט' : 'pick a script'}</option>
              {scripts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <button className="tab" disabled={!scriptToAdd} onClick={addScript}>
              {lang === 'he' ? 'הוסף' : 'Add'}
            </button>
          </div>
          <div className="sync-field-hint" dir="auto">
            {lang === 'he'
              ? 'רץ בכל מעבר, לתמיד. אין לו מצב סיום. להפסקה — הסר אותו או כבה את התוכנית.'
              : 'Runs on every pass, forever. It has no completion state. Remove it or deactivate the plan to stop it.'}
          </div>
        </div>
        <div className="sync-row sync-formrow">
          <Typeahead
            lang={lang}
            label={lang === 'he' ? 'הוסף את כל הטבלאות של מסך' : 'Add every table a form needs'}
            hint={lang === 'he' ? 'פותר את המסך לטבלאות שלו, ואז בוחרים מה להוסיף.'
                                : 'Resolves the form to its tables, then you pick which to add.'}
            items={formItems} total={formTotal}
            value={pickedForm} onPick={setPickedForm}
            onQuery={onFormQuery}
          />
          <button className="tab" disabled={!pickedForm} onClick={() => {
            PCMAdapter.resolveSyncForm(tenantId, plan.id, pickedForm.name)
              .then((d) => setPreview({
                ename: d.ename,
                // An outer-only table costs columns if dropped, not rows, so it
                // starts UNCHECKED and the operator opts in.
                tables: d.tables.map((t) => ({ ...t, checked: !t.outer_only })),
              }))
              .catch((e) => onError(String(e.message || e)));
          }}>{lang === 'he' ? 'פתור מסך' : 'Resolve form'}</button>
        </div>
      </div>
      <div className="sync-row">
        <button className="tab" onClick={save}>
          {dirty ? (lang === 'he' ? 'שמור סדר (יש שינויים)' : 'Save order (unsaved changes)')
                 : (lang === 'he' ? 'שמור סדר' : 'Save order')}
        </button>
      </div>
      {preview && (
        <div className="sync-panel">
          <div dir="auto">{preview.ename}: {preview.tables.length} tables</div>
          {preview.tables.map((t, i) => (
            <label key={t.tname} className={cls('sync-member', t.runnable === false && 'sync-member-unrunnable')}>
              <input type="checkbox" checked={t.checked} onChange={() => {
                const next = preview.tables.slice();
                next[i] = { ...t, checked: !t.checked };
                setPreview({ ...preview, tables: next });
              }}/>
              <span dir="auto">{t.tname}</span>
              {t.outer_only && (
                <span className="sync-field-hint" dir="auto">
                  {lang === 'he' ? 'מגיע רק דרך outer join — השמטתו מאבדת עמודות, לא שורות'
                                 : 'reachable only by an outer join — dropping it loses columns, not rows'}
                </span>
              )}
              {t.runnable === false && <span className="sync-warn">no generated script</span>}
            </label>
          ))}
          <button className="tab" onClick={() => {
            const have = new Set(rows.filter((r) => r.kind === 'table').map((r) => r.tname));
            const add = preview.tables.filter((t) => t.checked && !have.has(t.tname))
              .map((t) => ({ kind: 'table', tname: t.tname, source_ename: preview.ename,
                             runnable: t.runnable }));
            setRows(rows.concat(add));
            setPreview(null);
          }}>{lang === 'he' ? 'הוסף' : 'Add checked'}</button>
          <button className="tab" onClick={() => setPreview(null)}>Cancel</button>
        </div>
      )}
    </div>
  );
}

// --- 8.3 --------------------------------------------------------------
/* Spec 5.7's subtitle, and the phrase that spec sets in bold. ONE literal
 * per language: the emphasis is LOCATED in it at runtime rather than the
 * sentence being split into hand-typed fragments, so not one character of
 * the wording can drift from what the spec froze -- the same reason NextPass
 * above assembles its strip from literal fragments instead of interpolating.
 * A phrase that is not found renders the whole sentence unemphasised, which
 * loses a bold and never a word.
 */
const SCRIPT_DANGER = {
  he: 'קוד SQLI שנשמר כאן רץ ללא השגחה על מערכת ה-ERP בייצור. סקריפט שמור אינו עושה דבר עד שהוא מחובר. החיבור הוא מה שגורם לו לרוץ.',
  en: 'Operator SQLI stored here is arbitrary code that runs unattended on the production ERP. A saved script does nothing until it is attached. Attaching it is what makes it run.',
};
const SCRIPT_DANGER_EMPH = {
  he: 'רץ ללא השגחה על מערכת ה-ERP בייצור',
  en: 'arbitrary code that runs unattended on the production ERP',
};

/* Two registers in one pane, and the distinction is the point. THIS sentence
 * is the loud one -- it is at --text, not the --text-soft every other section
 * subtitle uses, because a stored script is arbitrary code on the production
 * ERP. The row-level `Attached to nothing -- never runs` stays muted: an
 * unattached script is inert, and a library where everything shouts teaches
 * an operator to ignore all of it.
 * <strong> carries no dir: an element with a dir attribute is a bidi
 * ISOLATE, so tagging a phrase in the middle of the Hebrew sentence with one
 * would re-order the sentence around it. Bare, it is transparent to the
 * algorithm and the rendering is byte-for-byte what the unsplit string gives.
 */
function DangerSub({ lang }) {
  const key = lang === 'he' ? 'he' : 'en';
  const s = SCRIPT_DANGER[key];
  const phrase = SCRIPT_DANGER_EMPH[key];
  const at = s.indexOf(phrase);
  return (
    <div className="sync-sub sync-sub-danger" dir="auto">
      {at < 0 ? s : (
        <>
          {s.slice(0, at)}
          <strong>{phrase}</strong>
          {s.slice(at + phrase.length)}
        </>
      )}
    </div>
  );
}

/* Where one script is attached, in one sentence. Spec 5.7's four rows stay
 * four sentences because the roles ARE four different server shapes -- a
 * member row of its own, a column on a table member, and a replacement of
 * that table's emission -- and the differences are exactly what an operator
 * has to understand before attaching anything.
 *
 * Returns a STRING, not JSX, because the disabled Delete button's `title`
 * joins these and a title attribute takes text. The Latin plan and table
 * names sit inside the Hebrew sentences with no bidi isolate, and that is
 * checked rather than overlooked: none of these four strings puts a neutral
 * character -- a quote, a bracket, a trailing period -- against the name,
 * and it is the neutrals, not the Latin run itself, that a missing isolate
 * moves. (Compare the chooser's own heading below, which does quote a name.)
 */
/* Why an attach outcome is unavailable. Two blockers, two sentences, and
 * they are never interchangeable: a plan with no table at all sends the
 * operator to Add a table, while a script that is not this table's fork sends
 * them to the Fork button in Plan contents. Collapsing them into one message
 * would name the wrong recovery half the time.
 *
 * NOT_A_FORK is the only wording on this branch not lifted from the brief or
 * the spec -- the brief's OPTIONS array carries no gate at all, so there was
 * nothing to lift. Its codepoints are listed in the task-9 report.
 */
const NO_TABLE = { he: 'אין טבלה בתוכנית', en: 'this plan has no table' };
const NOT_A_FORK = {
  he: 'רק פיצול של טבלה שבתוכנית יכול להחליף את הסקריפט המיוצר שלה',
  en: 'only a fork of a table in this plan can replace its generated script',
};

/* ONE definition, two renderings, so the two can never drift.
 *
 * The literal fragments below concatenate to exactly the sentences above --
 * assembled rather than interpolated, the same technique NextPass's `sentence`
 * uses and for the same reason: emphasising, or here ISOLATING, a value must
 * cost nothing of the wording.
 *
 * `v(...)` marks an interpolated NAME. A plan name is operator free text: a
 * plan called `Nightly (main)` ends an RTL sentence with a bidi NEUTRAL, and
 * an unisolated neutral takes the paragraph's own direction -- the closing
 * paren lands at the far left of the line. CLAUDE.md's standing rule already
 * requires `dir="auto"` per element on operator- and Priority-sourced values;
 * this is that rule applied, with no isolate codepoints spliced into a frozen
 * literal. Table names carry it too: uniform, and the rule is about where the
 * value came from, not about which characters happen to be in it today.
 */
function attachmentParts(a, lang) {
  const he = lang === 'he';
  const v = (s) => ({ v: s });
  if (a.role === 'standalone') {
    return he ? ['רץ בכל מעבר של תוכנית ', v(a.plan_name)]
              : ['Runs on every pass of plan ', v(a.plan_name)];
  }
  if (a.role === 'pre') {
    return he ? ['רץ לפני ', v(a.tname), ' בתוכנית ', v(a.plan_name)]
              : ['Runs before ', v(a.tname), ' in plan ', v(a.plan_name)];
  }
  if (a.role === 'post') {
    return he ? ['רץ אחרי ', v(a.tname), ' בתוכנית ', v(a.plan_name)]
              : ['Runs after ', v(a.tname), ' in plan ', v(a.plan_name)];
  }
  return he ? ['מחליף את הסקריפט המיוצר של ', v(a.tname), ' בתוכנית ', v(a.plan_name)]
            : ['Replaces ', v(a.tname), "'s generated script in plan ", v(a.plan_name)];
}

/* The TEXT rendering. The disabled Delete button's `title` joins these and a
 * title attribute takes text, not elements -- which is why the parts exist
 * rather than the line simply being JSX.
 */
function attachmentLine(a, lang) {
  return attachmentParts(a, lang)
    .map((p) => (typeof p === 'string' ? p : p.v)).join('');
}

/* The ELEMENT rendering, one isolate per name. */
function AttachmentLine({ a, lang }) {
  return (
    <div className="sync-attach-line" dir="auto">
      {attachmentParts(a, lang).map((p, i) => (
        typeof p === 'string'
          ? <React.Fragment key={i}>{p}</React.Fragment>
          : <span key={i} dir="auto">{p.v}</span>
      ))}
    </div>
  );
}

/* The one help sentence that embeds a code fragment, and the reason it needs
 * handling: `= ' < >` are bidi NEUTRALS. Unisolated inside a Hebrew run they
 * take the paragraph's own direction, and the quotes end up on the wrong
 * sides of the name. MEASURED in the browser before this was added: the
 * CLOSING quote rendered at x=1042 and the OPENING quote at x=1107 -- swapped.
 * The fragment is LOCATED in the sentence and wrapped, exactly as the section
 * subtitle's emphasis is: not one character changes, and a sentence that does
 * not contain it renders whole. In English it is a no-op -- an LTR isolate
 * inside an LTR paragraph.
 */
const CODE_FRAGMENT = "source = 'T_<TNAME>'";
function helpText(help) {
  const at = help.indexOf(CODE_FRAGMENT);
  if (at < 0) return help;
  return (
    <>
      {help.slice(0, at)}
      <span dir="ltr">{CODE_FRAGMENT}</span>
      {help.slice(at + CODE_FRAGMENT.length)}
    </>
  );
}

/* One entry point, three named outcomes. The three attachments stay three
 * concepts because they ARE three server shapes -- a member row, a column on
 * a table member, and a replacement of that table's emission -- and the
 * differences are exactly what the operator has to understand. What they get
 * is one place to start from.
 *
 * Every outcome edits the local `rows` array and is saved by Save order.
 * Having one of the three write immediately while the other two waited
 * would be its own trap.
 */
function AttachChooser({ lang, script, plan, rows, setRows, onClose }) {
  const tableRows = rows
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => r.kind === 'table');
  const [role, setRole] = useState('standalone');
  // Keyed on the TABLE NAME, never on an array index. `rows` is shared with
  // MembersPanel, so a reorder or a removal while this is open would move the
  // index out from under us and write the hook onto a different member -- and
  // a hook on the wrong table fails silently, unlike one on a script member
  // (which service.py's _TABLE_ONLY_FIELDS rejects loudly).
  const [target, setTarget] = useState(tableRows.length ? tableRows[0].r.tname : '');
  const noTables = tableRows.length === 0;
  const he = lang === 'he';

  // The server does not accept an arbitrary script as a table's fork:
  // replace_members refuses unless `kind == 'fork'` AND `forked_from_tname`
  // is that very table (app/syncplans/service.py:310-316). That refusal
  // arrives at Save order and rejects the WHOLE PUT, taking every other
  // pending edit with it -- and a set fork has no undo in the members
  // editor, which renders it as a display-only span (only pre/post are
  // selects with a `none`). Both fields are already on every script here
  // (_script_to_dict), so the option is gated with the reason BEFORE the
  // click, exactly as the Delete guard is.
  const forkTname = script.kind === 'fork' ? script.forked_from_tname : null;
  const forkTarget = forkTname && tableRows.some(({ r }) => r.tname === forkTname)
    ? forkTname : null;
  const blockedOf = (key) => {
    if (key === 'standalone') return null;
    if (key === 'fork') {
      if (forkTarget) return null;
      return noTables ? NO_TABLE[he ? 'he' : 'en'] : NOT_A_FORK[he ? 'he' : 'en'];
    }
    return noTables ? NO_TABLE[he ? 'he' : 'en'] : null;
  };
  // A fork has exactly ONE legal table -- the one it was forked from -- so
  // for that role the table is stated, not chosen.
  const roleTables = role === 'fork'
    ? tableRows.filter(({ r }) => r.tname === forkTarget)
    : tableRows;
  const first = roleTables.length ? roleTables[0].r.tname : '';

  useEffect(() => {
    // The selected outcome can stop being legal under us -- the last table
    // leaves the plan, or the fork's table does. The role is deliberately
    // LEFT WHERE THE OPERATOR PUT IT and Attach is disabled instead (see the
    // button below). An earlier version fell back to `standalone` here, and
    // that was worse than the problem: the checked radio moved on its own and
    // Attach then wrote a standalone member nobody had chosen. Doing nothing
    // and refusing the click is the only honest answer -- the blocked
    // option's own reason line already says why the button is dead.
    // The fork role is FORCED, not healed: its one legal table is the only
    // value the server would accept, so writing it cannot land on the wrong
    // table -- the failure the rest of this component exists to prevent.
    if (role === 'fork') {
      if (forkTarget && target !== forkTarget) setTarget(forkTarget);
      return;
    }
    // Everything else fills an EMPTY target only. Opened against a plan with
    // no table, then a table is added in the members editor beside this: the
    // <select> appears showing its first option while state still holds '',
    // and Apply would write nothing while the screen said otherwise. It must
    // never RE-POINT a target that was picked and has since left the plan --
    // that is the wrong-table write, and that case keeps the
    // close-without-writing path in apply() below.
    if (!target && first) setTarget(first);
  }, [role, target, first, forkTarget]);

  const apply = () => {
    if (role === 'standalone') {
      // A silent skip on a duplicate, the same way addTable refuses a table
      // already in the plan. Nothing dedupes SCRIPT members: replace_members'
      // `seen_tables` covers tables only (service.py:301-303). And a
      // standalone member has no completion state -- two of them means this
      // script runs twice on every driver pass, against the production ERP,
      // for as long as the plan is active, with nothing on screen saying so.
      if (!rows.some((r) => r.kind === 'script'
                         && Number(r.script_id) === script.id)) {
        setRows(rows.concat([{ kind: 'script', script_id: script.id }]));
      }
      onClose();
      return;
    }
    const i = rows.findIndex((r) => r.kind === 'table' && r.tname === target);
    // The table left the plan while this was open. Closing without writing is
    // the only safe answer; re-opening shows the current list.
    if (i < 0) { onClose(); return; }
    const field = role === 'fork' ? 'fork_script_id'
                : role === 'pre' ? 'pre_script_id' : 'post_script_id';
    const next = rows.slice();
    next[i] = { ...rows[i], [field]: script.id };
    setRows(next);
    onClose();
  };

  const OPTIONS = [
    { key: 'standalone',
      label: he ? 'הרץ בפני עצמו' : 'Run it on its own',
      help: he ? 'רץ בכל מעבר, לתמיד; לעולם לא מסתיים. להפסקה — הסר אותו או כבה את התוכנית.'
               : 'Runs on every pass, forever; never completes. Stop it by removing it or deactivating the plan.' },
    { key: 'pre',
      label: he ? 'הרץ לפני טבלה' : 'Run it before a table',
      help: he ? 'נעלם כשהטבלה יוצאת מהתוכנית. כישלון מדלג על הטבלה במעבר הזה במקום לסרוק אותה לא מוכנה.'
               : 'Disappears when that table leaves the plan. A failure skips its table this pass rather than walking it unpopulated.' },
    { key: 'post',
      label: he ? 'הרץ אחרי טבלה' : 'Run it after a table',
      help: he ? 'נעלם כשהטבלה יוצאת מהתוכנית.'
               : 'Disappears when that table leaves the plan.' },
    { key: 'fork',
      label: he ? 'השתמש בו במקום הסקריפט המיוצר' : "Use it instead of a table's generated script",
      help: he ? "הפיצול מחזיק את המעטפת; שבירת שורת ה-source = 'T_<TNAME>' בתוכו שוברת בשקט את מעקב הריצות."
               : "The fork owns the envelope; breaking the source = 'T_<TNAME>' line inside it breaks that table's bookkeeping silently." },
  ];

  return (
    <div className="sync-panel sync-attach">
      {/* The script NAME is isolated in its own dir="auto" span rather than
          interpolated into the heading string: it is operator-entered text,
          it is Latin inside a Hebrew heading, and it sits between two
          quotation marks -- the exact case where an unisolated run drags the
          quotes to the wrong end of the name. Same reason NextPass marks the
          plan name and not the numbers. */}
      <h3 dir="auto">
        {he ? 'חיבור "' : 'Attach "'}
        <span dir="auto">{script.name}</span>
        {'"'}
      </h3>
      {!plan && (
        <div className="sync-warn" dir="auto">
          {he ? 'בחר תוכנית קודם — חיבור הוא עריכה של תוכנית.'
              : 'Pick a plan first — attaching is a plan edit.'}
        </div>
      )}
      {plan && OPTIONS.map((o) => {
        // One string, computed once: whether this outcome is available and,
        // if not, the reason -- which is never the same sentence for two
        // different blockers.
        const why = blockedOf(o.key);
        return (
          <label key={o.key}
                 className={cls('sync-attach-opt', role === o.key && 'sel', why && 'off')}>
            <input type="radio" name="attach-role" value={o.key}
                   checked={role === o.key}
                   disabled={!!why}
                   onChange={() => setRole(o.key)}/>
            <span className="sync-attach-name" dir="auto">{o.label}</span>
            <span className="sync-field-hint" dir="auto">{helpText(o.help)}</span>
            {why && (
              <span className="sync-warn-inline" dir="auto">{why}</span>
            )}
          </label>
        );
      })}
      {plan && role !== 'standalone' && roleTables.length > 0 && (
        <div className="sync-fieldset">
          <label className="sync-field-label" dir="auto">{he ? 'טבלה' : 'Table'}</label>
          <select className="set-input" value={target}
                  onChange={(e) => setTarget(e.target.value)}>
            {roleTables.map(({ r }) => (
              <option key={r.tname} value={r.tname}>{r.tname}</option>
            ))}
          </select>
        </div>
      )}
      <div className="sync-row">
        {/* Refused, not silently redirected. `blockedOf(role)` is the same
            call that greys the option and prints its reason one row up, so a
            dead Attach is already explained on screen -- and the operator
            recovers by picking a live outcome, which is their choice rather
            than one this component made for them. */}
        <button className="tab" disabled={!plan || !!blockedOf(role)} onClick={apply}>
          {he ? 'חבר' : 'Attach'}
        </button>
        <button className="tab" onClick={onClose}>{he ? 'ביטול' : 'Cancel'}</button>
      </div>
      {plan && (
        <div className="sync-field-hint" dir="auto">
          {he ? 'נשמר עם "שמור סדר" בתוכן התוכנית.'
              : 'Saved by "Save order" in Plan contents.'}
        </div>
      )}
    </div>
  );
}

function ScriptsPanel({ tenantId, lang, plan, scripts, rows, setRows, reload, onError }) {
  const [sel, setSel] = useState(null);
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [attaching, setAttaching] = useState(null);
  const he = lang === 'he';
  const pick = (s) => { setSel(s); setName(s.name); setBody(s.body); };
  // The chooser must not outlive its script. Another row's Delete reloads the
  // library, and a chooser still holding the removed row would write a
  // script_id no longer in sync_scripts into the plan -- an edit that looks
  // saved here and fails at Save order.
  const attachTo = attaching ? scripts.find((s) => s.id === attaching.id) || null : null;
  // Every script id the OPEN plan editor currently references, saved or not.
  // `attachments` is computed server-side from SAVED members, so between a
  // successful Attach and Save order a freshly attached script still had no
  // attachment to show -- and the card went on saying `Attached to nothing --
  // never runs`, which is the one sentence that is now false. An operator who
  // reads that as failure clicks Attach again.
  //
  // The false line is REMOVED rather than replaced with a pending one: a
  // "pending" sentence is copy nobody has written, and claiming the
  // attachment outright would be a different lie. Silence beside Save order's
  // own `(unsaved changes)` label is the honest state. A script attached in
  // the selected plan already has a real attachment, so this only ever fires
  // on an unsaved edit.
  const pendingIds = new Set();
  rows.forEach((r) => {
    [r.script_id, r.pre_script_id, r.post_script_id, r.fork_script_id]
      .forEach((id) => { if (id != null) pendingIds.add(Number(id)); });
  });
  const save = () => {
    const p = sel
      ? PCMAdapter.updateSyncScript(tenantId, sel.id, { name, body })
      : PCMAdapter.createSyncScript(tenantId, { name, body });
    // The server's 400 quotes the offending line. Show it VERBATIM --
    // collapsing it to "save failed" throws away the whole point.
    p.then(() => { setSel(null); setName(''); setBody(''); reload(); })
     .catch((e) => onError(String(e.message || e)));
  };
  return (
    <div className="sync-panel">
      <h3 dir="auto">{he ? '3 · ספריית סקריפטים' : '3 · Script library'}</h3>
      <DangerSub lang={lang}/>
      {scripts.map((s) => {
        const att = s.attachments || [];
        return (
          /* One script is one card: the identity row, then every fact about
             it under the same border. The facts go BELOW the row rather than
             beside it because a script can hold several attachments and a
             single flex line cannot hold several sentences -- ungrouped they
             would also read as belonging to the row beneath. */
          <React.Fragment key={s.id}>
            <div className={cls('sync-script-row', sel && sel.id === s.id && 'sel')}
                 onClick={() => pick(s)}>
              <div className="sync-member">
                <span dir="auto">{s.name}</span>
                <span className="mono">{s.kind}</span>
                <button className="tab" onClick={(e) => {
                  e.stopPropagation();
                  setAttaching(s);
                }}>
                  {he ? 'חבר' : 'Attach'}
                </button>
                {/* The SERVER already refuses this with a 409
                    (service.delete_script, app/syncplans/service.py:536-561).
                    Disabling the button is not a new guard -- it is the same
                    refusal, said before the click instead of after it, with the
                    attachments as the reason rather than a bare error banner.
                    The reason is on the card as well as in this title: a
                    disabled control fires no mouse events in some browsers, so
                    a title is the second channel here and never the only one. */}
                <button className="tab" disabled={att.length > 0}
                        title={att.length > 0
                          ? att.map((a) => attachmentLine(a, lang)).join(' · ')
                          : undefined}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!await confirmAction(he ? `למחוק את הסקריפט "${s.name}"?` : `Delete script "${s.name}"?`, { lang, danger: true, confirmLabel: he ? 'מחיקה' : 'Delete' })) return;
                          PCMAdapter.deleteSyncScript(tenantId, s.id).then(reload)
                            .catch((err) => onError(String(err.message || err)));
                        }}>×</button>
              </div>
              {/* Drift is a fact about the script, so it joins the facts
                  under the row instead of squeezing the name and both
                  buttons off the end of a 340px column. */}
              {s.drifted && (
                <div className="sync-drift" dir="auto">
                  {he ? 'הסקריפט המיוצר השתנה מאז הפיצול'
                      : 'the generated script has changed since this fork'}
                </div>
              )}
              {att.length === 0 && !pendingIds.has(s.id) && (
                <div className="sync-field-hint" dir="auto">
                  {he ? 'לא מחובר לשום דבר — לעולם לא רץ'
                      : 'Attached to nothing — never runs'}
                </div>
              )}
              {att.map((a, i) => <AttachmentLine key={i} a={a} lang={lang}/>)}
            </div>
            {/* Under the row it belongs to, not at the foot of the list: with
                twenty scripts in the library a chooser at the bottom asks the
                operator to remember which row they clicked.
                What actually forces a fresh `role`/`target` for a second
                script is this Fragment's own key -- the chooser moves to a
                different position in the tree and remounts. The key below is
                belt-and-braces (measured: removing it changes nothing today),
                and it is what keeps that true if the pane is ever hoisted out
                of the row it currently sits under. */}
            {attachTo && attachTo.id === s.id && (
              <AttachChooser key={attachTo.id} lang={lang} script={attachTo} plan={plan}
                             rows={rows} setRows={setRows}
                             onClose={() => setAttaching(null)}/>
            )}
          </React.Fragment>
        );
      })}
      {/* Two stacked boxes with a placeholder in one of them is not a form
          (spec 5.7). The name box is a real <label> around its input; the
          code box takes a caption instead, because DeleteAwareEditor renders
          its DELETE and UPDATE warnings as siblings of the textarea and a
          <label> wrapped round all three would read those warnings out as
          the field's name. */}
      <label className="sync-fieldset sync-script-name">
        <span className="sync-field-label" dir="auto">{he ? 'שם' : 'Name'}</span>
        <input className="set-input" value={name} dir="auto" placeholder="SCRIPT_NAME"
               onChange={(e) => setName(e.target.value)}/>
      </label>
      <div className="sync-fieldset sync-script-code">
        <span className="sync-field-label" dir="auto">{he ? 'קוד' : 'Code'}</span>
        <DeleteAwareEditor value={body} onChange={setBody} lang={lang}/>
      </div>
      {/* A .sync-row, like every other action row on this page: bare in the
          panel's column flex these two stretched to the full panel width. */}
      <div className="sync-row">
        <button className="tab" disabled={!name.trim() || !body.trim()} onClick={save}>
          {sel ? (he ? 'עדכן' : 'Update') : (he ? 'צור' : 'Create')}
        </button>
        {sel && <button className="tab" onClick={() => { setSel(null); setName(''); setBody(''); }}>
          {he ? 'חדש' : 'New'}
        </button>}
      </div>
    </div>
  );
}

// --- 8.4 --------------------------------------------------------------
// The "see what will execute" requirement. Rendered VERBATIM: no
// reformatting, no syntax colouring, no wrapping.
// `members` is the list currently in the editor, NOT the saved one: the
// operator opens this pane after reordering something, and showing them the
// stored plan would answer the previous question.
function GeneratedPanel({ tenantId, lang, plan, tables, members, onError }) {
  const [text, setText] = useState('');
  const [picked, setPicked] = useState(null);
  // plan.env, never the page's env: plans are listed across every env, so the
  // selected plan's env and the page's can differ, and the preview route
  // answers 400 on the mismatch. The page's env is not a prop here at all --
  // out of scope is the only way it cannot be reached for by mistake.
  const load = () => PCMAdapter.getSyncPlanPreview(tenantId, plan.id, plan.env, members)
    .then(setText).catch((e) => onError(String(e.message || e)));
  if (!plan) return null;
  return (
    <div className="sync-panel">
      <h3 dir="auto">{lang === 'he' ? 'הקוד שירוץ' : 'The code that will run'}</h3>
      <div className="sync-sub" dir="auto">
        {lang === 'he'
          ? 'בדיוק הסקריפט שהדרייבר ימשוך עבור התוכנית הזו, כולל שינויים שלא נשמרו למעלה. מילה במילה — ללא עיצוב מחדש.'
          : 'Exactly the script the driver will fetch for this plan, including your unsaved edits above. Verbatim — no reformatting.'}
      </div>
      <div className="sync-row">
        <button className="tab" onClick={load}>{lang === 'he' ? 'טען דרייבר' : 'Load driver'}</button>
      </div>
      {/* Its own row, because its label says "Or": on one line with Load
          driver the two read as a sequence rather than as alternatives. */}
      <div className="sync-row sync-formrow">
        <Typeahead
          lang={lang}
          label={lang === 'he' ? 'או טען סקריפט של טבלה בודדת' : "Or load one table's script"}
          items={bundleItems(tables, lang)}
          value={picked}
          onPick={setPicked}
          emptyText={tablesEmptyText(tables, lang)}
        />
        <button className="tab" disabled={!picked}
                title={!picked ? (lang === 'he' ? 'בחר טבלה מהרשימה' : 'pick a table from the list') : undefined}
                onClick={() => {
                  // The pick is deliberately NOT cleared: the pane below now
                  // holds that table's script, and the name in the box is
                  // what says which one.
                  PCMAdapter.getBundleScript(`T_${picked.name}`)
                    .then(setText).catch((e) => onError(String(e.message || e)));
                }}>{lang === 'he' ? 'טען טבלה' : 'Load table script'}</button>
      </div>
      <pre className="sync-code" dir="ltr">{text}</pre>
    </div>
  );
}

/* Cadence is entered in human units and stored in seconds. The unit menu
 * exists because "86400" is not a thing an operator should have to type or
 * read, and minutes matter: a hot table may want 30 of them. */
const UNITS = [
  { key: 's', secs: 1, he: 'שניות', en: 'seconds' },
  { key: 'm', secs: 60, he: 'דקות', en: 'minutes' },
  { key: 'h', secs: 3600, he: 'שעות', en: 'hours' },
  { key: 'd', secs: 86400, he: 'ימים', en: 'days' },
];

function splitInterval(secs) {
  if (secs == null) return { n: '', unit: 'h' };
  // 's' (secs: 1) always divides evenly, so this always returns from
  // inside the loop -- a value the UI itself never produces (PUT
  // /api/v1/refresh-policy accepts any positive integer) still round-trips
  // exactly, instead of being mislabelled as minutes.
  for (const u of [...UNITS].reverse()) {
    if (secs % u.secs === 0) return { n: String(secs / u.secs), unit: u.key };
  }
  return { n: String(secs), unit: 's' };
}

// The backend (RefreshSettingsRequest / RefreshPolicyRequest, both in
// app/routers/sync_plans.py) rejects anything <= 0, and a fractional
// interval_s (e.g. "1.5" typed against a unit that doesn't divide evenly)
// would otherwise reach it as a JSON float and 422 rather than 400 -- so
// both forms in this panel gate on the RESULT being a whole positive
// number, not on the raw input looking like one.
function isPositiveInt(v) {
  return Number.isFinite(v) && Number.isInteger(v) && v > 0;
}

function unitSecsOf(u) {
  return (UNITS.find((x) => x.key === u.unit) || UNITS[1]).secs;
}

// The unit KEY is a Latin token ('d'), which reads fine inside an English
// sentence and is a foreign fragment inside a Hebrew one -- every visible
// label needs its Hebrew string. UNITS already carries the word for both, so
// nothing new is authored here; the same words already fill the unit menu.
function unitWordOf(u, lang) {
  const found = UNITS.find((x) => x.key === u.unit) || UNITS[1];
  return lang === 'he' ? found.he : found.en;
}

/* The measured cost of one HTTP post to the ERP. Read in two places -- the
 * budget field's live spend estimate below, and the next-pass strip -- so it
 * is declared once rather than typed twice. The `1.39 s/post` inside the
 * DISPLAY strings stays a literal: spec 5.3.1 freezes that wording. */
const SECS_PER_POST = 1.39;

// One line per mode, shown under the picker for whichever is selected. The
// topup WARNING below the form stays as it is -- this is the plain
// description, not the warning.
const MODE_HELP = {
  '': { en: "Chosen by the server from the table's window column.",
        he: 'נבחר על ידי השרת לפי עמודת החלון של הטבלה.' },
  full: { en: 'Re-walk the whole table every time. Correct, slowest.',
          he: 'סריקה מחדש של כל הטבלה בכל פעם. נכון, והאיטי ביותר.' },
  topup: { en: 'Only rows past the last anchor. Silently misses updates and deletes.',
           he: 'רק שורות מעבר לנקודת העוגן. מפספס בשקט עדכונים ומחיקות.' },
  page: { en: 'For a table with no window column: walk it by row offset.',
          he: 'לטבלה ללא עמודת חלון: סריקה לפי היסט שורות.' },
  off: { en: 'Never refresh this table.', he: 'לא לרענן את הטבלה הזו לעולם.' },
};

/* The three call sites want different halves of the same list, so each
 * builds its own rows and DISABLES what it cannot use rather than hiding it.
 */
/* Spec section 9's ceiling 1: a fresh install has two different emptinesses and
 * they send an operator to different places. `bundle_mounted` is the ONLY thing
 * that tells them apart, so every picker passes this as its emptyText.
 */
function tablesEmptyText(tables, lang) {
  const he = lang === 'he';
  if (!tables) return he ? 'טוען…' : 'loading…';
  if (tables.bundle_mounted === false) {
    return he ? 'חבילת הסקריפטים אינה מותקנת' : 'the script bundle is not mounted';
  }
  if (tables.tables.length === 0) {
    return he ? 'עוד לא נסרקה אף טבלה עבור חברה זו'
              : 'no table has been walked for this company yet';
  }
  return he ? 'אין התאמות' : 'no matches';
}

function bundleItems(tables, lang) {
  if (!tables) return [];
  return tables.tables.map((t) => ({
    name: t.tname,
    hint: t.run_state || (lang === 'he' ? 'טרם נסרקה' : 'no runs yet'),
    disabled: t.in_bundle === false,
    disabledWhy: lang === 'he' ? 'אין סקריפט מיוצר' : 'no generated script',
  }));
}

function walkedItems(tables, lang) {
  if (!tables) return [];
  return tables.tables.map((t) => ({
    name: t.tname,
    hint: t.run_state || (lang === 'he' ? 'טרם נסרקה' : 'no runs yet'),
    // is_due reads form_runs. A cadence on a table with no run row is
    // stored and then does nothing, which is the worst of both.
    disabled: t.run_state == null,
    disabledWhy: lang === 'he' ? 'טרם נסרקה — קצב לא ישפיע'
                               : 'never walked; a cadence would do nothing',
  }));
}

function RefreshPolicyPanel({ tenantId, env, lang, tables, onDefaults, onError }) {
  const [data, setData] = useState(null);
  const [n, setN] = useState('');
  const [unit, setUnit] = useState('h');
  // The picked ITEM, never a bare name: Typeahead's clear-effect reads
  // `was.name`, so a string here loses the parent-clears-box behaviour and
  // strands stale text under a disabled Save button. Set only via onPick.
  const [picked, setPicked] = useState(null);
  const [mode, setMode] = useState('');
  const [fullN, setFullN] = useState('');
  const [fullUnit, setFullUnit] = useState('d');

  // Global knobs (PUT /api/v1/refresh-settings). Human units in, seconds
  // out, same pattern as the per-table cadence above. These are seeded from
  // data.defaults on every load -- see the effect below -- so Save always
  // diffs against what the server actually has, not a stale local guess.
  const [gInterval, setGInterval] = useState({ n: '', unit: 'h' });
  const [gStruct, setGStruct] = useState({ n: '', unit: 'h' });
  const [gRunMaxAge, setGRunMaxAge] = useState({ n: '', unit: 'h' });
  const [gBudget, setGBudget] = useState('');
  const [gMaxFail, setGMaxFail] = useState('');
  const [gFull, setGFull] = useState({ n: '', unit: 'd' });

  // A request generation counter, same as mirror-freshness.jsx: if
  // tenantId/env change while an earlier load() is still in flight, that
  // earlier response must not overwrite state for a selection the operator
  // has already moved past -- otherwise the pane shows another company's or
  // another env's policy rows.
  const reqIdRef = useRef(0);
  // Always the CURRENT tenantId/env, refreshed on every render -- unlike the
  // tenantId/env captured in load()'s own closure (fixed for that call's
  // whole life), this ref reflects a selection change that happens while the
  // call is still in flight. The counter proves a response isn't stale
  // relative to a LATER call; this proves the response's own key still
  // matches what's on screen right now, which the counter alone cannot.
  const selectionRef = useRef({ tenantId, env });
  selectionRef.current = { tenantId, env };
  const load = useCallback(async () => {
    // Bump the generation even on the empty-selection branch below: an
    // in-flight call for a prior, complete selection must be invalidated
    // the instant the operator clears one side of it, not just when they
    // pick a new complete pair.
    const myId = ++reqIdRef.current;
    // Both are required: an operator who types ENV before picking a
    // company would otherwise fire getRefreshPolicy(null, env) and see an
    // error before they can even choose the tenant.
    if (!env || !tenantId) return;
    const myKey = { tenantId, env };
    try {
      // adminToken: true -- this pane is on the admin-gated control room,
      // and require_admin wants an admin JWT, not the tenant one.
      const result = await PCMAdapter.getRefreshPolicy(tenantId, env);
      if (myId !== reqIdRef.current) return; // superseded by a later call
      if (myKey.tenantId !== selectionRef.current.tenantId
          || myKey.env !== selectionRef.current.env) return; // selection moved on
      setData(result);
    } catch (e) {
      if (myId !== reqIdRef.current) return;
      if (myKey.tenantId !== selectionRef.current.tenantId
          || myKey.env !== selectionRef.current.env) return;
      onError(String(e && e.message ? e.message : e));
    }
  }, [tenantId, env, onError]);

  // Clear stale rows the moment the selection changes, before the new
  // load() even lands -- each policy row below carries an Enable/Disable
  // button that saves {env, tname: <that row's table>} using the CURRENT
  // env prop, so a stale row from another selection left on screen is a
  // live write hazard (writes one selection's table under another
  // selection's env), not just a display glitch.
  useEffect(() => { setData(null); }, [tenantId, env]);

  useEffect(() => { load(); }, [load]);

  // Re-seed the global-knobs form every time fresh defaults arrive --
  // including right after saveGlobal()'s own reload, which is what proves
  // the displayed values are what got stored, not just echoed back locally.
  useEffect(() => {
    if (!data) return;
    const df = data.defaults;
    setGInterval(splitInterval(df.interval_s));
    setGStruct(splitInterval(df.structure_interval_s));
    setGRunMaxAge(splitInterval(df.run_max_age_s));
    setGBudget(String(df.post_budget));
    setGMaxFail(String(df.max_failures));
    setGFull(df.full_interval_s ? splitInterval(df.full_interval_s) : { n: '', unit: 'd' });
  }, [data]);

  // Seeded from the STORED default, not a literal 24h. The dangerous slip on
  // this form is leaving the unit on 'seconds' and saving 10 -- a table
  // walked every ten seconds against the live ERP. An untouched form must
  // reproduce the default rather than invent something violent.
  //
  // This effect belongs HERE, with the other hooks, above the early returns:
  // a hook placed beside the add-form JSX would change the hook count between
  // renders and blank the page.
  useEffect(() => {
    if (!data) return;
    const s = splitInterval(data.defaults.interval_s);
    setN(s.n); setUnit(s.unit);
  }, [data]);

  // The full-walk field must show the PICKED table's own override, and clear
  // when the pick changes. PUT /refresh-policy is a full upsert, not a patch
  // -- the same reason the Enable/Disable button below carries
  // full_interval_s along -- so a blank field here SAVES a null and silently
  // wipes an override the operator never touched. Blank interval_s cannot do
  // that (Save is disabled unless it is a positive integer); blank
  // full_interval_s means "use the global", which is a real, destructive
  // value. Keyed on the picked name and on data, so a save-then-reload
  // re-seeds from what the server actually stored.
  useEffect(() => {
    const p = picked && data
      ? data.policies.find((x) => x.tname === picked.name)
      : null;
    const s = p && p.full_interval_s ? splitInterval(p.full_interval_s) : { n: '', unit: 'd' };
    setFullN(s.n); setFullUnit(s.unit);
  }, [picked, data]);

  // The next-pass strip's budget number comes from HERE rather than from a
  // second getRefreshPolicy call on the page. Two reasons, and the second is
  // the one that matters: the page's own fetch was keyed on (tenant, env)
  // only, so after saving a new post budget the strip went on declaring the
  // OLD one until Reload -- a false declarative sentence on the acceptance
  // path. saveGlobal()'s load() now feeds the strip the stored value.
  useEffect(() => {
    if (onDefaults) onDefaults(data ? data.defaults : null);
  }, [data, onDefaults]);

  // Resolves true only when the write landed AND the reload after it did.
  // The add form reads that to clear its pick; a failed save must leave the
  // operator's pick exactly where it was, behind the error.
  const save = async (row) => {
    try {
      await PCMAdapter.putRefreshPolicy(tenantId, row);
      await load();
      return true;
    } catch (e) { onError(String(e && e.message ? e.message : e)); return false; }
  };

  if (!env) {
    return (
      <div className="sync-panel">
        <h3 dir="auto">{lang === 'he' ? '1 · כל כמה זמן' : '1 · How often'}</h3>
        <div className="muted" dir="auto">
          {lang === 'he'
            ? 'עוד לא נסרקה אף טבלה עבור חברה זו, ולכן אין סביבה להגדיר עבורה קצב.'
            : 'No table has been walked for this company yet, so there is no environment to set a cadence for.'}
        </div>
      </div>
    );
  }
  if (!data) return <div className="sync-panel muted" dir="auto">
    {lang === 'he' ? 'טוען…' : 'Loading…'}
  </div>;
  // A row the adaptive rule owns (set_by 'adaptive', scripts/adapt_refresh.py)
  // is counted, not listed: with its knob on that is ~3,000 rows per company,
  // and each listed row's Enable/Disable is a full-upsert PUT that hands the
  // row to a person -- one click would silently take it from the rule.
  const people = data.policies.filter((p) => !p.set_by);
  const adaptiveCount = data.policies.length - people.length;
  const secs = Number(n) * unitSecsOf({ unit });
  // Blank here means "use the global setting above" -- unlike the global
  // row, 0 is not a value the per-table override can hold (PUT
  // /refresh-policy rejects it), so a blank field must send null, never 0.
  const fullSecs = fullN === '' ? null : Number(fullN) * unitSecsOf({ unit: fullUnit });
  const fullValid = fullN === '' || isPositiveInt(fullSecs);

  const orig = data.defaults;
  const gIntervalSecs = Number(gInterval.n) * unitSecsOf(gInterval);
  const gStructSecs = Number(gStruct.n) * unitSecsOf(gStruct);
  const gRunMaxAgeSecs = Number(gRunMaxAge.n) * unitSecsOf(gRunMaxAge);
  const gBudgetNum = Number(gBudget);
  const gMaxFailNum = Number(gMaxFail);
  // Blank = off (0). The one knob where 0 is a value, so validity is
  // "blank, or a positive whole number of seconds".
  const gFullSecs = gFull.n === '' ? 0 : Number(gFull.n) * unitSecsOf(gFull);
  const gFullValid = gFull.n === '' || isPositiveInt(gFullSecs);
  const gValid = isPositiveInt(gIntervalSecs) && isPositiveInt(gStructSecs)
    && isPositiveInt(gRunMaxAgeSecs) && isPositiveInt(gBudgetNum) && isPositiveInt(gMaxFailNum)
    && gFullValid;
  const gChanged = gIntervalSecs !== orig.interval_s
    || gStructSecs !== orig.structure_interval_s
    || gRunMaxAgeSecs !== orig.run_max_age_s
    || gBudgetNum !== orig.post_budget
    || gMaxFailNum !== orig.max_failures
    || gFullSecs !== (orig.full_interval_s || 0);
  // The spend line tracks the BUDGET FIELD's live value, not the stored
  // default -- an operator sizing a maintenance window needs to see the
  // consequence of what they are about to type, not what is already saved.
  // Under the one-minute poll, a pass this small is routine (a few hundred
  // posts, minutes not hours) -- Math.round(...) straight to hours rounds
  // that to "0h", so the display drops to minutes below one hour, and to
  // a "<1 min" floor below one minute rather than rounding THAT to "0 min".
  const spendSecs = isPositiveInt(gBudgetNum) ? gBudgetNum * SECS_PER_POST : null;
  const spendHours = spendSecs != null ? Math.round(spendSecs / 3600) : null;
  const spendMinutes = spendSecs != null ? Math.round(spendSecs / 60) : null;

  const saveGlobal = async () => {
    const body = {};
    if (gIntervalSecs !== orig.interval_s) body.interval_s = gIntervalSecs;
    if (gStructSecs !== orig.structure_interval_s) body.structure_interval_s = gStructSecs;
    if (gRunMaxAgeSecs !== orig.run_max_age_s) body.run_max_age_s = gRunMaxAgeSecs;
    if (gBudgetNum !== orig.post_budget) body.post_budget = gBudgetNum;
    if (gMaxFailNum !== orig.max_failures) body.max_failures = gMaxFailNum;
    if (gFullSecs !== (orig.full_interval_s || 0)) body.full_interval_s = gFullSecs;
    try {
      // Only the changed keys are sent -- the endpoint treats an omitted
      // field as "leave alone" (RefreshSettingsRequest defaults to None).
      await PCMAdapter.putRefreshSettings(tenantId, body);
      await load();
    } catch (e) { onError(String(e && e.message ? e.message : e)); }
  };

  const durationRow = (label, desc, val, setVal) => (
    <div className="sync-fieldset">
      <label className="sync-field-label" dir="auto">{label}</label>
      <div className="sync-row">
        <input className="set-input" aria-label={label} size={4} value={val.n}
               onChange={(e) => setVal((v) => ({ ...v, n: e.target.value }))}/>
        <select className="set-input" aria-label={`${label} (${lang === 'he' ? 'יחידה' : 'unit'})`} value={val.unit}
                onChange={(e) => setVal((v) => ({ ...v, unit: e.target.value }))}>
          {UNITS.map((u) => (
            <option key={u.key} value={u.key}>{lang === 'he' ? u.he : u.en}</option>
          ))}
        </select>
      </div>
      <div className="sync-field-hint" dir="auto">{desc}</div>
    </div>
  );

  return (
    <div className="sync-panel">
      <h3 dir="auto">{lang === 'he' ? '1 · כל כמה זמן' : '1 · How often'}</h3>
      <div className="sync-sub" dir="auto">
        {lang === 'he'
          ? 'קובע את הסנכרון המלא ואת כל הטבלאות. זה מה שפועל כרגע.'
          : 'Governs the fallback sync and every table. In force today.'}
      </div>

      {/* Required, not decorative: PUT /api/v1/refresh-settings accepts
          tenant_id and deliberately does not scope the write by it, so an
          admin with OfTov selected has every reason to read "post budget"
          as OfTov's budget. It is not. */}
      <div className="sync-sub2" dir="auto">
        {lang === 'he'
          ? 'הגדרות כלליות — חלות על כל ההתקנה, לא רק על חברה זו.'
          : 'Global settings — these apply to the whole installation, not just this company.'}
      </div>

      {durationRow(
        lang === 'he' ? 'קצב ברירת מחדל' : 'Default cadence',
        lang === 'he'
          ? 'כמה זמן אחרי שטבלה מסיימת עד שהיא נחשבת שוב לדחופה. חל על כל טבלה שאין לה קצב משלה למטה.'
          : 'How long after a table finishes before it is due again. Applies to every table without its own cadence below.',
        gInterval, setGInterval)}
      {durationRow(
        lang === 'he' ? 'קצב סריקה מלאה' : 'Full re-walk cadence',
        lang === 'he'
          ? 'מעולם לא רץ מול ה-ERP. הסקריפטים יודעים לענות על בדיקה, אך החבילה חייבת להתחדש קודם — זה קורה מעצמו בהרצת /driver הבאה. סקריפט שלא חודש עוצר את הריצה של אותה טבלה בקול, בלי לשלוח דבר. הפעילו רק כשאפשר לצפות במעבר הראשון. ריק (נשמר כ-0): ללא בדיקות — כל טבלה שהגיע זמנה נסרקת במלואה, כמו היום. ברגע שנקבע ערך, טבלה שהגיע זמנה נבדקת קודם: ה-ERP מדווח מספר אחד לכל חלון מפתחות, ורק חלונות ששונים נשלחים מחדש. זה הזמן המרבי בין סריקות מלאות בכל מקרה — בדיקה לא רואה עריכה שעמודת העד שלה לא רואה, והסריקה המלאה היא מה שתופס אותן.'
          : "Never run against the ERP. The scripts can answer a check, but the bundle must regenerate first — that happens on its own at the next /driver pass. An un-regenerated script aborts that table's run loudly, shipping nothing. Turn this on only when you can watch the first pass. Blank (stored as 0): no checks — every due table is re-walked in full, as today. Once set, a due table is first checked: the ERP reports one number per key window and only windows that differ are re-shipped. This is how long a table may go between full re-walks regardless — a check cannot see an edit its witness column cannot see; the full re-walk is what catches those.",
        gFull, setGFull)}
      {/* full_interval_for() (app/refresh/policy.py) prefers a PER-TABLE
          full_interval_s override over this global field, so a blank
          global field does not mean checks are off installation-wide --
          only that no table without its own override gets them. Checking
          data.policies here is what keeps this line from lying to an
          operator who set an override on one table and left the global
          field blank. */}
      {gFull.n === '' && !data.policies.some((p) => p.full_interval_s) && (
        <div className="sync-field-hint muted" dir="auto">
          {lang === 'he' ? 'בדיקות כבויות.' : 'Checks are off.'}
        </div>
      )}
      {durationRow(
        lang === 'he' ? 'קצב סנכרון מבנה' : 'Structure sync cadence',
        lang === 'he'
          ? 'כל כמה זמן נקרא מחדש מילון העמודות כדי לזהות טבלה שהשתנתה.'
          : 'How often the column dictionary is re-read to catch a changed table.',
        gStruct, setGStruct)}
      {durationRow(
        lang === 'he' ? 'גיל ריצה מרבי' : 'Run max age',
        lang === 'he'
          ? 'אחרי הזמן הזה, מעבר שלא דיווח בחזרה נמחק כדי שהמעבר הבא יוכל להתחיל.'
          : 'After this long, a pass that never reported back is written off so the next one can start.',
        gRunMaxAge, setGRunMaxAge)}
      <div className="sync-fieldset">
        <label className="sync-field-label" dir="auto">
          {lang === 'he' ? 'תקציב בקשות למעבר' : 'Post budget per pass'}
        </label>
        <div className="sync-row">
          <input className="set-input" aria-label={lang === 'he' ? 'תקציב בקשות למעבר' : 'Post budget per pass'} size={6} value={gBudget}
                 onChange={(e) => setGBudget(e.target.value)}/>
          {/* Indentation below is deliberately left as it was: spec 5.3.1
              freezes this estimate "exactly as it is", and the unspaced `Nh`
              band it spells is NOT the strip's spaced `N h`. Re-indenting
              would put every one of these frozen strings in a diff hunk. */}
          <span className="muted">
          {spendSecs == null
            ? (lang === 'he' ? '(ערך לא תקין)' : '(invalid value)')
            : spendSecs < 60
              ? (lang === 'he' ? '(פחות מדקה במדידה של 1.39 שניות לבקשה)'
                                : '(<1 min at the measured 1.39 s/post)')
              : spendSecs < 3600
                ? (lang === 'he' ? `(כ-${spendMinutes} דקות במדידה של 1.39 שניות לבקשה)`
                                  : `(≈${spendMinutes} min at the measured 1.39 s/post)`)
                : (lang === 'he' ? `(כ-${spendHours} שעות במדידה של 1.39 שניות לבקשה)`
                                  : `(≈${spendHours}h at the measured 1.39 s/post)`)}
          </span>
        </div>
        <div className="sync-field-hint" dir="auto">
          {lang === 'he'
            ? 'כמה בקשות HTTP מעבר אחד רשאי להוציא לפני שהוא נעצר וממשיך במעבר הבא.'
            : 'How many HTTP posts one pass may spend before it stops and resumes next time.'}
        </div>
      </div>
      <div className="sync-fieldset">
        <label className="sync-field-label" dir="auto">
          {lang === 'he' ? 'כשלים מרביים' : 'Max failures'}
        </label>
        <div className="sync-row">
          <input className="set-input" aria-label={lang === 'he' ? 'כשלים מרביים' : 'Max failures'} size={4} value={gMaxFail}
                 onChange={(e) => setGMaxFail(e.target.value)}/>
        </div>
        <div className="sync-field-hint" dir="auto">
          {lang === 'he'
            ? 'אחרי כל כך הרבה כשלים רצופים הטבלה מושארת בצד עד שמישהו יבדוק אותה.'
            : 'After this many consecutive failures a table is left alone until someone looks at it.'}
        </div>
      </div>
      {/* A greyed button with no explanation is the failure this page is
          fixing, so it says which of the two things it is waiting for. */}
      <div className="sync-row">
        <button className="tab" disabled={!gValid || !gChanged}
                title={!gValid
                  ? (lang === 'he' ? 'כל ערך חייב להיות מספר שלם חיובי'
                                   : 'every value must be a positive whole number')
                  : (!gChanged ? (lang === 'he' ? 'לא שונה דבר' : 'nothing changed')
                               : undefined)}
                onClick={saveGlobal}>
          {lang === 'he' ? 'שמור הגדרות' : 'Save settings'}
        </button>
      </div>

      <div className="sync-sub2" dir="auto">
        {lang === 'he' ? 'קצב לטבלה בודדת — חריגה מברירת המחדל עבור טבלה אחת.'
                       : 'Per-table cadence — override the default for one table.'}
      </div>
      {/* The parenthesised number is the LIVE default, not a literal: a pane
          that renders nothing is what made an operator conclude no table had
          a cadence AND that the feature did not exist.
          English keeps splitInterval's KEY -- `1d`, spec section 11's shape.
          Hebrew takes the WORD, spec section 5.3.2's `(24 שעות)` shape,
          because a Latin `d` is not a Hebrew label. */}
      {data.policies.length === 0 && (
        <div className="muted" dir="auto">
          {lang === 'he'
            ? `לאף טבלה אין קצב משלה. כל הטבלאות משתמשות בברירת המחדל שלמעלה (${splitInterval(orig.interval_s).n} ${unitWordOf(splitInterval(orig.interval_s), lang)}).`
            : `No table has its own cadence. Every table uses the default above (${splitInterval(orig.interval_s).n}${splitInterval(orig.interval_s).unit}).`}
        </div>
      )}

      {adaptiveCount > 0 && (
        <div className="sync-field-hint muted" dir="auto">
          {lang === 'he'
            ? `${adaptiveCount} טבלאות במרווח אוטומטי`
            : `${adaptiveCount} tables on an adaptive interval`}
        </div>
      )}

      {people.map((p) => {
        const s = splitInterval(p.interval_s);
        return (
          <div className="sync-member" key={p.tname}>
            <span dir="auto">{p.tname}</span>
            {/* The mode explains itself where it is DISPLAYED, not only
                where it was chosen. `|| MODE_HELP['']` is not decoration:
                refresh_policy.mode is a free string column, so a value
                stored before VALID_MODES existed would throw on
                undefined[lang] and blank the page. */}
            <span title={(MODE_HELP[p.mode || ''] || MODE_HELP[''])[lang === 'he' ? 'he' : 'en']}>
              {s.n}{s.unit} · {p.mode || (lang === 'he' ? 'ברירת מחדל' : 'default')}
              {p.full_interval_s
                ? ` · ${lang === 'he' ? 'מלא' : 'full'} ${splitInterval(p.full_interval_s).n}${splitInterval(p.full_interval_s).unit}`
                : ''}
            </span>
            {/* full_interval_s must ride along here too -- PUT
                /refresh-policy is a full upsert, not a patch, so an Enable/
                Disable click that omits it silently clears the table's own
                full-walk override back to "use the global". */}
            <button className="tab" onClick={() => save({
              env, tname: p.tname, interval_s: p.interval_s,
              mode: p.mode, enabled: !p.enabled, full_interval_s: p.full_interval_s,
            })}>
              {p.enabled
                ? (lang === 'he' ? 'השבת' : 'Disable')
                : (lang === 'he' ? 'הפעל' : 'Enable')}
            </button>
          </div>
        );
      })}

      <div className="sync-row sync-formrow">
        {/* The name comes from a server-provided list, so the old TNAME_RE
            client check is gone with the text box: a table that does not
            exist can no longer be typed in at all. */}
        <Typeahead
          lang={lang}
          label={lang === 'he' ? 'טבלה' : 'Table'}
          items={walkedItems(tables, lang)}
          value={picked}
          onPick={setPicked}
          emptyText={tablesEmptyText(tables, lang)}
        />
        <div className="sync-fieldset">
          <label className="sync-field-label" dir="auto">
            {lang === 'he' ? 'כל' : 'Every'}
          </label>
          <div className="sync-row">
            <input className="set-input" aria-label={lang === 'he' ? 'כל (מספר)' : 'Every (number)'} value={n} size={4}
                   onChange={(e) => setN(e.target.value)}/>
            <select className="set-input" aria-label={lang === 'he' ? 'כל (יחידה)' : 'Every (unit)'} value={unit}
                    onChange={(e) => setUnit(e.target.value)}>
              {UNITS.map((u) => (
                <option key={u.key} value={u.key}>{lang === 'he' ? u.he : u.en}</option>
              ))}
            </select>
          </div>
        </div>
        {/* 'topup' is selectable but never a default: it is fail-silent by
            construction, so the warning below fires the moment it is picked. */}
        <div className="sync-fieldset">
          <label className="sync-field-label" dir="auto">
            {lang === 'he' ? 'אופן' : 'How'}
          </label>
          <select className="set-input" aria-label={lang === 'he' ? 'אופן הסריקה' : 'Scan mode'} value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="">{lang === 'he' ? 'ברירת מחדל' : 'default'}</option>
            <option value="full">full</option>
            <option value="topup">topup</option>
            <option value="page">page</option>
            <option value="off">off</option>
          </select>
          <div className="sync-field-hint" dir="auto">{MODE_HELP[mode][lang === 'he' ? 'he' : 'en']}</div>
        </div>
        <div className="sync-fieldset">
          <label className="sync-field-label" dir="auto">
            {lang === 'he' ? 'סריקה מלאה לפחות כל' : 'Full re-walk at least every'}
          </label>
          <div className="sync-row">
            <input className="set-input" aria-label={lang === 'he' ? 'סריקה מלאה לפחות כל (מספר)' : 'Full re-walk at least every (number)'} value={fullN} size={4}
                   onChange={(e) => setFullN(e.target.value)}/>
            <select className="set-input" aria-label={lang === 'he' ? 'סריקה מלאה לפחות כל (יחידה)' : 'Full re-walk at least every (unit)'} value={fullUnit}
                    onChange={(e) => setFullUnit(e.target.value)}>
              {UNITS.map((u) => (
                <option key={u.key} value={u.key}>{lang === 'he' ? u.he : u.en}</option>
              ))}
            </select>
          </div>
          <div className="sync-field-hint" dir="auto">
            {lang === 'he' ? 'ריק: לפי ההגדרה הכללית שלמעלה.' : 'Blank: the global setting above.'}
          </div>
        </div>
        {/* Clears the pick only when the write AND its reload landed -- a
            failed save must leave the operator's pick where it was. No field
            needs an explicit reset: `n`/`unit` re-seed from fresh data, and
            `fullN`/`fullUnit` re-seed from the pick, which is now null. */}
        <button className="tab"
                disabled={!picked || !isPositiveInt(secs) || !fullValid}
                title={!picked ? (lang === 'he' ? 'בחר טבלה מהרשימה' : 'pick a table from the list')
                               : (!isPositiveInt(secs) ? (lang === 'he' ? 'הזן מספר שלם חיובי' : 'enter a positive whole number')
                                                        : (!fullValid ? (lang === 'he' ? 'סריקה מלאה: הזן מספר שלם חיובי או השאר ריק' : 'full re-walk: enter a positive whole number, or leave it blank') : undefined))}
                onClick={async () => {
                  const ok = await save({
                    env, tname: picked.name, interval_s: secs,
                    mode: mode || null, enabled: true, full_interval_s: fullSecs,
                  });
                  if (ok) setPicked(null);
                }}>
          {lang === 'he' ? 'שמור' : 'Save'}
        </button>
      </div>

      {isPositiveInt(secs) && secs < 60 && (
        <div className="sync-hintline" dir="auto">
          {lang === 'he'
            ? 'קצר יותר ממחזור הדרייבר (דקה) — הטבלה פשוט תיסרק בכל מעבר.'
            : "Shorter than the driver's poll (one minute) — this table will simply be walked on every pass."}
        </div>
      )}

      {mode === 'topup' && (
        <div className="sync-warn" dir="auto">
          {lang === 'he'
            ? 'רענון-קצה מפספס בשקט כל עדכון או מחיקה מתחת לנקודת העוגן, וכל שורה חדשה שנוספת למפתח ישן. אשרו רק לטבלה שנמדדה כמוסיפה-בלבד על מפתח מונוטוני משלה.'
            : 'A top-up silently misses every update or delete below the anchor, and every insert at an old key position. Enrol only a table measured to be append-mostly on its own monotone key.'}
        </div>
      )}
    </div>
  );
}

/* What the NEXT pass will do -- not what is happening now. Live pass state
 * is on Mirror freshness, which is tenant-gated and correctly so.
 *
 * Composed entirely from data the page already holds. The old code put the
 * same fact in a `sync-warn` box, which told the operator something was
 * broken when the fallback is the normal, correct state.
 */

// 15500 reads as a part number; 15,500 reads as a quantity -- and this is
// the number an operator sizes a maintenance window against.
const commas = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/* The sentences are ASSEMBLED FROM LITERAL FRAGMENTS rather than interpolated
 * into one template string, so that emphasising the values costs not one
 * character of the wording: every fragment below is a plain string and
 * concatenating the array reproduces the sentence exactly. Only the runtime
 * values are marked -- they are what an operator scans this strip for. The
 * plan NAME carries dir="auto" because it is operator-entered text that can
 * be Latin inside a Hebrew sentence, where an unisolated run drags the
 * quotation marks with it; a bare number needs no isolation.
 */
const emph = (v, dir) => ({ v, dir });
const sentence = (parts) => parts.map((p, i) => (
  typeof p === 'string' ? p
    : <b className="sync-strip-v" key={i} dir={p.dir}>{p.v}</b>
));

function NextPass({ lang, plans, env, envs, envsLoaded, defaults }) {
  const he = lang === 'he';
  // Spec section 9's ceiling 2: a tenant with zero envs has nothing for the
  // driver to walk at all, and saying that beats the fallback sentence with
  // an em dash where its budget should be. Gated on envsLoaded, never on
  // `!env` alone -- env is also empty for the one request between picking a
  // company and the answer landing, and a "nothing to walk" that flashes on
  // every page load is worse than no sentence.
  const nothingToWalk = envsLoaded && envs.length === 0;
  const active = plans.find((p) => p.is_active && p.env === env) || null;
  const budget = defaults ? defaults.post_budget : null;
  const secs = budget ? budget * SECS_PER_POST : null;
  // Both unknowns fall back to the same em dash. An empty string here would
  // render "( at the measured 1.39 s/post)" on the first paint of every
  // visit -- before the policy call lands -- and again whenever it fails.
  const cost = secs == null ? '—'
    : secs < 60 ? (he ? 'פחות מדקה' : '<1 min')
    : secs < 3600 ? (he ? `כ-${Math.round(secs / 60)} דקות`
                        : `≈${Math.round(secs / 60)} min`)
    // `N h`, spaced, per spec 5.2 -- which the brief's `Nh` contradicted, and
    // which also makes this band agree with the two above it. The UNSPACED
    // `Nh` in the global-settings spend line below is a DIFFERENT string that
    // spec 5.3.1 freezes as-is; the two deliberately do not match.
    : (he ? `כ-${Math.round(secs / 3600)} שעות`
          : `≈${Math.round(secs / 3600)} h`);
  const budgetText = budget == null ? '—' : commas(budget);
  const tables = active ? active.members.filter((m) => m.kind === 'table').length : 0;
  const scripts = active ? active.members.filter((m) => m.kind === 'script').length : 0;

  return (
    <div className="sync-strip">
      <div className="sync-strip-main">
        <h3 dir="auto">{he ? 'המעבר הבא' : 'The next pass'}</h3>
        {nothingToWalk && (
          <p dir="auto">
            {he
              ? 'עוד לא נסרקה אף טבלה עבור חברה זו, ולכן אין לדרייבר מה לסרוק.'
              : 'No table has been walked for this company yet, so the driver has nothing to walk.'}
          </p>
        )}
        {!active && !nothingToWalk && (
          <p dir="auto">{sentence(he
            ? ['אין תוכנית פעילה. הדרייבר עובר על כל טבלה שהגיע זמנה, לפי הפיגור היחסי הגדול ביותר, עד ',
               emph(budgetText),
               ` בקשות למעבר (${cost} לפי מדידה של 1.39 שניות לבקשה).`]
            : ['No active plan. The driver walks every table that is due, most-overdue first, up to ',
               emph(budgetText),
               ` posts per pass (${cost} at the measured 1.39 s/post).`])}</p>
        )}
        {active && (
          <p dir="auto">{sentence(he
            ? ['תוכנית "', emph(active.name, 'auto'), '" פעילה: ',
               emph(tables), ' טבלאות, ', emph(scripts), ' סקריפטים.']
            : ['Plan "', emph(active.name, 'auto'), '" is active: ',
               emph(tables), ' tables, ', emph(scripts), ' scripts.'])}</p>
        )}
        {active && scripts > 0 && (
          <p dir="auto">
            {he
              ? 'הסקריפטים רצים בכל מעבר — בקצב של פעם בדקה, 1,440 פעמים ביום.'
              : 'The scripts run on every pass — under the one-minute poll that is 1,440 runs a day.'}
          </p>
        )}
        <p className="muted sync-strip-meta" dir="auto">
          {he
            ? 'מצב הריצה הנוכחית נמצא בעמוד "טריות המראה".'
            : 'Live pass state is on Mirror freshness.'}
        </p>
      </div>
      {/* Spec §7.6: the budget is the one real number the mock's deep-sky
          card can show -- it sizes ONE pass, so the label says so. */}
      <div className="sync-budget">
        <div className="sync-budget-label">{he ? 'תקציב בקשות למעבר' : 'Post budget per pass'}</div>
        <div className="sync-budget-n">{budgetText}<span className="sync-budget-unit"> {he ? 'בקשות' : 'posts'}</span></div>
        <div className="sync-budget-cost">{cost === '—' ? '' : (he ? `≈ ${cost} · 1.39 שניות לבקשה` : `≈ ${cost} · 1.39 s/post`)}</div>
      </div>
    </div>
  );
}

function SyncControlPage({ lang }) {
  const [tenants, setTenants] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  // NEVER a text input. The server answers which envs this tenant has table
  // runs for; one env is derived and shown as a label, several give a
  // <select>. Typing 'oftov' when the env is 'oftovp' produced a plan that
  // could never match anything, and nothing on screen said so.
  const [envs, setEnvs] = useState([]);
  // Whether `envs` is an ANSWER or merely its initial value. "This company
  // has no environment" and "the plans call has not landed yet" are the same
  // empty array, and only one of them is worth a sentence in the strip.
  const [envsLoaded, setEnvsLoaded] = useState(false);
  const [env, setEnv] = useState('');
  // The strip's budget number, fed by RefreshPolicyPanel's own load rather
  // than by a second identical request from here -- see its onDefaults
  // effect for why that also fixes a stale number after a save.
  const [defaults, setDefaults] = useState(null);
  // Fetched once per (company, env), not per keystroke: the list is ~3,200
  // names and the server side of it scans a ~3,234-entry directory.
  // `null` means "not loaded yet"; a loaded-but-unmounted bundle is
  // {bundle_mounted: false}.
  const [tables, setTables] = useState(null);
  const [plans, setPlans] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // Lifted out of MembersPanel: GeneratedPanel (8.4) previews these, unsaved.
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  // Separate from `error`: a successful re-sync is still an outcome the
  // operator needs to read in full (the skip list, the in-flight caveat),
  // but rendering it through the error banner told them a delete succeeded
  // by showing them a failure.
  const [notice, setNotice] = useState('');

  // Auto-pick the only company. An admin acting on the wrong company is the
  // mistake the picker exists to prevent, so several companies get NO
  // default -- but one company needs no decision at all.
  useEffect(() => {
    PCMAdapter.getTenants()
      .then((d) => {
        setTenants(d.tenants);
        if (d.tenants.length === 1) setTenantId(d.tenants[0].id);
      })
      .catch((e) => { setTenants([]); setError(String(e.message || e)); });
  }, []);

  // reload() reads tenantId from its own closure, fixed for that call's
  // whole life, and commits five pieces of page state unconditionally. Two
  // quick company switches and company A's answer lands after B's, replacing
  // B's plans, scripts and env list -- and setEnv's own comment below says
  // what a stale env costs: every panel is scoped by it and it writes to the
  // wrong company. Same reqId + live-tenant pair as RefreshPolicyPanel.
  const reloadIdRef = useRef(0);
  const reloadTenantRef = useRef(tenantId);
  reloadTenantRef.current = tenantId;

  const reload = useCallback(() => {
    // Bumped before the no-tenant branch, not after: an in-flight answer for
    // a prior company must be invalidated the instant the picker is cleared,
    // not only when another company is chosen.
    const myId = ++reloadIdRef.current;
    if (!tenantId) return Promise.resolve();
    const myTenant = tenantId;
    // env is omitted on purpose: the answer covers EVERY env, so a plan on a
    // mistyped env is listed rather than filtered into invisibility.
    return Promise.all([
      PCMAdapter.getSyncPlans(tenantId),
      PCMAdapter.getSyncScripts(tenantId),
    // Every write handler in this page (create/delete/activate/save/fork/
    // delete-script) calls reload() on success, so clearing the banner
    // HERE -- in one place, rather than sprinkling setError('') through
    // every handler -- is what stops a prior failure's message from
    // sitting on screen next to work that just succeeded.
    ]).then(([p, s]) => {
      if (myId !== reloadIdRef.current) return;       // superseded by a later reload
      if (myTenant !== reloadTenantRef.current) return; // the company moved on
      setPlans(p.plans);
      setScripts(s.scripts);
      setEnvs(p.envs);
      setEnvsLoaded(true);
      // Keep a chosen env only while it still exists; otherwise take the
      // first. Never leave a stale env in state -- every panel below is
      // scoped by it and a stale one writes to the wrong company.
      setEnv((cur) => (cur && p.envs.includes(cur) ? cur : (p.envs[0] || '')));
      setError(''); setNotice('');
    }).catch((e) => {
      // A failure for a company the operator has already left is noise, and
      // rendering it would put an error banner over the company now on
      // screen -- the same reason the success path is guarded.
      if (myId !== reloadIdRef.current) return;
      if (myTenant !== reloadTenantRef.current) return;
      setError(String(e.message || e));
    });
  }, [tenantId]);

  // `reload` is rebuilt only when tenantId changes, so this runs on exactly
  // that: a new company's env list is unknown again until its answer lands.
  useEffect(() => { setEnvsLoaded(false); reload(); }, [reload]);
  // Cleared on a COMPANY change, not on every reload: a reload after a save
  // must not throw away the operator's selection, but a company change must.
  //
  // `plans` is cleared with it, and that is not tidiness. The pre-select
  // effect below runs on whatever `plans` holds; between this clear and the
  // new company's answer landing that is still the OLD company's rows, so it
  // would pre-select a foreign plan under the new tenantId -- MembersPanel,
  // GeneratedPanel and Re-sync all rendering another company's plan, which
  // is the exact failure clearing the selection here exists to prevent.
  useEffect(() => { setSelectedId(null); setPlans([]); }, [tenantId]);

  // Pre-select: the active plan, else the only one, else none. The operator
  // should not have to discover that a plan row is clickable before anything
  // below it renders.
  //
  // `is_active` is read here WITHOUT an env test on purpose, and it is not
  // the mistake it resembles: this picks which row to open, not which plan
  // governs this env. An active plan on another env is exactly the row an
  // operator most needs to land on -- its own row carries its env and its
  // `no data for this env` marker. The env-scoped question is the strip's,
  // and NextPass answers it with `p.is_active && p.env === env`.
  useEffect(() => {
    if (selectedId && plans.some((p) => p.id === selectedId)) return;
    const active = plans.find((p) => p.is_active);
    setSelectedId(active ? active.id : (plans.length === 1 ? plans[0].id : null));
  }, [plans, selectedId]);

  // The table lookup, once per (company, env) rather than per keystroke.
  // env is resolved from the server's own list before this fires -- the
  // adapter omits a falsy env and the route then 422s loudly, which is the
  // point: a placeholder like '' or 'undefined' would answer for nothing
  // and read as an empty company.
  useEffect(() => {
    if (!tenantId || !env) { setTables(null); return undefined; }
    let live = true;
    PCMAdapter.getSyncLookupTables(tenantId, env)
      .then((d) => { if (live) setTables(d); })
      .catch((e) => { if (live) { setTables(null); setError(String(e.message || e)); } });
    return () => { live = false; };
  }, [tenantId, env]);

  const plan = plans.find((p) => p.id === selectedId) || null;
  useEffect(() => { setRows(plan ? plan.members : []); },
            [plan && plan.id, plan && plan.updated_at]);

  const resync = async () => {
    if (!plan) return;
    const n = plan.members.filter((m) => m.kind === 'table').length;
    if (!await confirmAction(
      lang === 'he'
        ? `לסנכרן מחדש את "${plan.name}"? פעולה זו מוחקת את רשומות form_runs של ${n} טבלאות בסביבה ${plan.env}, כך שבהרצה הבאה הדרייבר יסרוק אותן שוב.`
        : `Re-sync "${plan.name}"? This deletes the form_runs rows for ${n} table(s) `
          + `in ${plan.env}, so the next driver run walks them again.`,
      { lang, danger: true, confirmLabel: lang === 'he' ? 'סנכרון מחדש' : 'Re-sync' })) return;
    PCMAdapter.resyncSyncPlan(tenantId, plan.id)
      .then((d) => { setError(''); setNotice(
        `Deleted ${d.deleted} run row(s).`
        + (d.skipped_running.length
            ? ` SKIPPED (walk in flight): ${d.skipped_running.join(', ')} —`
              + ` re-run this after tonight's driver finishes.`
            : '')
        + ` A driver run that already fetched its script will still post its`
        + ` results, and re-sync clears the run ledger, not warehouse rows —`
        + ` for a table with no window axis a re-walk cannot remove rows that`
        + ` were deleted in Priority.`); })
      .catch((e) => { setNotice(''); setError(String(e.message || e)); });
  };

  if (tenants === null) return <div className="card"><div className="card-body dim">{lang === 'he' ? 'טוען…' : 'Loading…'}</div></div>;
  return (
    <div className="sync-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{lang === 'he' ? 'בקרת סנכרון' : 'Sync control'}</h1>
          <div className="page-sub">
            {lang === 'he'
              ? 'קצב רענון לכל טבלה ותקציב הבקשות ל-Priority.'
              : 'Per-table refresh cadence and the Priority request budget.'}
          </div>
        </div>
      </div>
      {/* No env text box anywhere on this page, by construction. The server
          answers which envs this tenant has table runs for, so there is
          nothing here to mistype: one env is a stated fact, several are a
          closed list, none is said in words. Production still carries a plan
          bound to 'oftov' because this was a free-text input. */}
      <div className="sync-header">
        <label className="sync-field">
          <span className="sync-field-label" dir="auto">
            {lang === 'he' ? 'חברה' : 'Company'}
          </span>
          <select className="set-input" value={tenantId || ''}
                  onChange={(e) => setTenantId(Number(e.target.value) || null)}>
            <option value="">{lang === 'he' ? 'בחר חברה' : 'pick a company'}</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.company_name}</option>)}
          </select>
        </label>
        {envs.length > 1 ? (
          <label className="sync-field">
            <span className="sync-field-label" dir="auto">
              {lang === 'he' ? 'סביבה' : 'Environment'}
            </span>
            {/* First entry pre-selected in reload(), not by a defaultValue:
                on a controlled select a defaultValue shows a name the state
                does not hold, and every panel below is scoped by the state. */}
            <select className="set-input" value={env}
                    onChange={(e) => setEnv(e.target.value)}>
              {envs.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>
        ) : (
          /* Stated on the row's baseline rather than stacked like the select
             beside it: a caption over a value in a control's own rhythm reads
             as a control whose box went missing, and the operator's next move
             is to click it. */
          <div className="sync-static">
            <span className="sync-field-label" dir="auto">
              {lang === 'he' ? 'סביבה:' : 'Environment:'}
            </span>
            {envs.length === 1
              ? <span className="mono" dir="auto">{envs[0]}</span>
              : <span className="muted" dir="auto">
                  {tenantId ? (lang === 'he' ? 'אין נתונים לחברה זו' : 'no data for this company')
                            : '—'}
                </span>}
          </div>
        )}
        <button type="button" className="btn" onClick={reload}>{lang === 'he' ? 'רענן' : 'Reload'}</button>
        {plan && <button type="button" className="btn" onClick={resync}>
          {lang === 'he' ? 'סנכרן מחדש' : 'Re-sync'}
        </button>}
      </div>
      <NextPass lang={lang} plans={plans} env={env} envs={envs}
                envsLoaded={envsLoaded} defaults={defaults}/>
      {/* The server's detail is shown VERBATIM: the validator quotes the
          offending line, and collapsing it to "save failed" throws away the
          whole reason it quotes it. */}
      {error && <div role="alert" className="set-error" dir="auto">{error}</div>}
      {notice && <div className="sync-notice" dir="auto">{notice}</div>}
      {/* Three numbered sections in ONE column, in the order that governs the
          ERP: cadence is what runs today, plans are an override of it, and
          the library is inert code. The numbered <h3>s live inside the
          panels; this container only holds the order, and the order is the
          documentation. */}
      <div className="sync-sections">
        {/* onDefaults is load-bearing, not a convenience: it is the ONLY
            source of the strip's budget number now. */}
        <RefreshPolicyPanel tenantId={tenantId} env={env} lang={lang}
                            tables={tables} onDefaults={setDefaults}
                            onError={setError}/>
        {/* Section 2 is three panels, not one: the plan list, the plan it
            selected, and the code that plan will emit. They are wrapped so
            the tighter gap between them says they belong together -- and so
            section 3 is a peer of the group, not of its last member. */}
        <div className="sync-section">
          <PlansPanel tenantId={tenantId} env={env} envs={envs} lang={lang} plans={plans}
                      selectedId={selectedId} onPick={setSelectedId}
                      reload={reload} onError={setError}/>
          {/* rows is lifted so GeneratedPanel can preview UNSAVED edits --
              that is the whole point of the pane. */}
          <MembersPanel tenantId={tenantId} lang={lang} plan={plan} scripts={scripts}
                        tables={tables} rows={rows} setRows={setRows}
                        reload={reload} onError={setError}/>
          {/* No env prop: the preview asks with plan.env, because a plan on
              another env would otherwise 400. See Task 8 Step 4. */}
          <GeneratedPanel tenantId={tenantId} lang={lang} plan={plan}
                          tables={tables} members={rows} onError={setError}/>
        </div>
        {/* Same lifted `rows` MembersPanel edits: attaching a script IS a
            plan edit, and it is saved by that panel's Save order. */}
        <ScriptsPanel tenantId={tenantId} lang={lang} plan={plan} scripts={scripts}
                      rows={rows} setRows={setRows}
                      reload={reload} onError={setError}/>
      </div>
    </div>
  );
}

Object.assign(window, { SyncControlPage });
